import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ArrowLeft, Play, Save, Gamepad2, Trash2, RotateCcw, Sparkles, Star, Trophy, Heart, Pause, Home, ChevronRight, ChevronDown, Zap, HelpCircle, Move, Target, Gift, Skull, Flag, Plus, GripVertical, Lock, Unlock, Volume2, VolumeX, ArrowUp, ArrowDown, ArrowLeftIcon, ArrowRight as ArrowRightIcon, LayoutGrid, Wand2, Package, Key, Coins, Bolt, X, Swords, Copy, Link } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

// ============ RETRO STYLING ============
const RetroStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');
  
  .retro-font {
    font-family: 'Press Start 2P', monospace;
  }
  
  .retro-glow {
    text-shadow: 0 0 10px currentColor, 0 0 20px currentColor, 0 0 30px currentColor;
  }
  
  .crt-effect {
    position: relative;
  }
  
  .crt-effect::before {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.15),
      rgba(0, 0, 0, 0.15) 1px,
      transparent 1px,
      transparent 2px
    );
    pointer-events: none;
    z-index: 10;
  }
  
  .crt-effect::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%);
    pointer-events: none;
    z-index: 11;
  }
  
  .pixel-border {
    border: 4px solid;
    border-image: linear-gradient(45deg, #8B5CF6, #06B6D4, #22C55E, #F59E0B) 1;
    image-rendering: pixelated;
  }
  
  .arcade-btn {
    position: relative;
    transform: perspective(100px) rotateX(5deg);
    box-shadow: 
      0 6px 0 rgba(0,0,0,0.3),
      0 8px 10px rgba(0,0,0,0.2),
      inset 0 1px 0 rgba(255,255,255,0.2);
    transition: all 0.1s ease;
  }
  
  .arcade-btn:active {
    transform: perspective(100px) rotateX(5deg) translateY(4px);
    box-shadow: 
      0 2px 0 rgba(0,0,0,0.3),
      0 3px 5px rgba(0,0,0,0.2),
      inset 0 1px 0 rgba(255,255,255,0.2);
  }
  
  @keyframes pixel-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.05); }
  }
  
  .pixel-pulse {
    animation: pixel-pulse 0.5s ease-in-out infinite;
  }
  
  @keyframes speed-trail {
    0% { opacity: 1; }
    100% { opacity: 0; transform: translateX(-10px); }
  }
`;

// ============ GAME CONSTANTS ============
const GRID_WIDTH = 16;
const GRID_HEIGHT = 12;
const BASE_CELL_SIZE = 40; // Desktop size

// Helper to calculate responsive cell size
const getResponsiveCellSize = (containerWidth) => {
  if (!containerWidth) return BASE_CELL_SIZE;
  const maxGridWidth = containerWidth - 32; // padding
  const cellSize = Math.floor(maxGridWidth / GRID_WIDTH);
  return Math.min(BASE_CELL_SIZE, Math.max(20, cellSize)); // min 20px, max 40px
};

// Asset pack system - designed for future expansion
const ASSET_PACKS = {
  default: {
    id: 'default',
    name: 'Temple Theme',
    description: 'Classic temple-themed objects',
    icon: '🏛️',
    color: '#8B5CF6',
    objects: {
      player: { emoji: '🏃', name: 'Hero', description: 'The character you control' },
      enemy: { emoji: '👾', name: 'Guardian', description: 'Avoid these!' },
      coin: { emoji: '⭐', name: 'Star', description: 'Collect for points' },
      wall: { emoji: '🧱', name: 'Stone', description: 'Blocks movement' },
      goal: { emoji: '🚪', name: 'Portal', description: 'Reach to win!' },
      // New collectibles
      heart: { emoji: '❤️', name: 'Heart', description: 'Extra life!' },
      key: { emoji: '🔑', name: 'Key', description: 'Unlocks the portal' },
      gem: { emoji: '💎', name: 'Gem', description: 'Bonus points!' },
      speedBoost: { emoji: '⚡', name: 'Speed Boost', description: 'Move faster!' },
      shield: { emoji: '🛡️', name: 'Shield', description: 'Blocks one hit!' }
    }
  },
  // Future asset packs - architecture ready
  egypt: {
    id: 'egypt',
    name: 'Ancient Egypt',
    description: 'Pyramids, scarabs, and mummies',
    icon: '🏺',
    color: '#F59E0B',
    locked: true,
    objects: {
      player: { emoji: '🧝', name: 'Explorer', description: 'Brave desert adventurer' },
      enemy: { emoji: '🐍', name: 'Serpent', description: 'Watch out for snakes!' },
      coin: { emoji: '💎', name: 'Gem', description: 'Ancient treasures' },
      wall: { emoji: '🏛️', name: 'Pyramid Block', description: 'Ancient stone' },
      goal: { emoji: '⚱️', name: 'Pharaoh Tomb', description: 'Discover the tomb!' },
      heart: { emoji: '🪬', name: 'Ankh', description: 'Life force!' },
      key: { emoji: '🗝️', name: 'Ancient Key', description: 'Unlocks the tomb' },
      gem: { emoji: '💠', name: 'Scarab', description: 'Sacred treasure!' },
      speedBoost: { emoji: '🌪️', name: 'Desert Wind', description: 'Swift movement!' },
      shield: { emoji: '🪖', name: 'Pharaoh Helm', description: 'Royal protection!' }
    }
  },
  space: {
    id: 'space',
    name: 'Space Adventure',
    description: 'Planets, rockets, and aliens',
    icon: '🚀',
    color: '#3B82F6',
    locked: true,
    objects: {
      player: { emoji: '🚀', name: 'Rocket', description: 'Your spaceship' },
      enemy: { emoji: '👽', name: 'Alien', description: 'Space creatures!' },
      coin: { emoji: '🌟', name: 'Stardust', description: 'Cosmic energy' },
      wall: { emoji: '🪨', name: 'Asteroid', description: 'Space rocks' },
      goal: { emoji: '🌍', name: 'Planet', description: 'Your destination!' },
      heart: { emoji: '🔋', name: 'Power Cell', description: 'Extra energy!' },
      key: { emoji: '🔮', name: 'Crystal', description: 'Activates warp gate' },
      gem: { emoji: '☄️', name: 'Comet', description: 'Rare find!' },
      speedBoost: { emoji: '🌠', name: 'Hyperdrive', description: 'Warp speed!' },
      shield: { emoji: '🛸', name: 'Force Field', description: 'Deflects aliens!' }
    }
  },
  nature: {
    id: 'nature',
    name: 'Nature Journey',
    description: 'Animals, trees, and rivers',
    icon: '🌿',
    color: '#22C55E',
    locked: true,
    objects: {
      player: { emoji: '🐿️', name: 'Squirrel', description: 'Quick forest friend' },
      enemy: { emoji: '🦊', name: 'Fox', description: 'Careful!' },
      coin: { emoji: '🌰', name: 'Acorn', description: 'Tasty treats' },
      wall: { emoji: '🌲', name: 'Tree', description: 'Forest barrier' },
      goal: { emoji: '🏠', name: 'Treehouse', description: 'Home sweet home!' },
      heart: { emoji: '🍄', name: 'Mushroom', description: 'Extra energy!' },
      key: { emoji: '🍂', name: 'Magic Leaf', description: 'Opens the way' },
      gem: { emoji: '🍯', name: 'Honey', description: 'Sweet reward!' },
      speedBoost: { emoji: '🌸', name: 'Pollen', description: 'Flutter fast!' },
      shield: { emoji: '🐚', name: 'Shell', description: 'Tough protection!' }
    }
  }
};

// Get current asset pack objects
const getAssetPack = (packId = 'default') => ASSET_PACKS[packId] || ASSET_PACKS.default;

// Object types with Temple-themed colors
const OBJECT_TYPES = {
  player: { 
    id: 'player', 
    color: '#22C55E', 
    glow: 'rgba(34, 197, 94, 0.5)',
    maxCount: 1,
    category: 'character'
  },
  enemy: { 
    id: 'enemy', 
    color: '#EF4444', 
    glow: 'rgba(239, 68, 68, 0.5)',
    maxCount: 10,
    category: 'hazard'
  },
  coin: { 
    id: 'coin', 
    color: '#F59E0B', 
    glow: 'rgba(245, 158, 11, 0.5)',
    maxCount: 20,
    category: 'collectible'
  },
  wall: { 
    id: 'wall', 
    color: '#6B7280', 
    glow: 'rgba(107, 114, 128, 0.3)',
    maxCount: 50,
    category: 'obstacle'
  },
  goal: { 
    id: 'goal', 
    color: '#3B82F6', 
    glow: 'rgba(59, 130, 246, 0.5)',
    maxCount: 1,
    category: 'objective'
  },
  // New collectibles
  heart: {
    id: 'heart',
    color: '#EC4899',
    glow: 'rgba(236, 72, 153, 0.5)',
    maxCount: 3,
    category: 'powerup'
  },
  key: {
    id: 'key',
    color: '#FBBF24',
    glow: 'rgba(251, 191, 36, 0.5)',
    maxCount: 3,
    category: 'collectible'
  },
  gem: {
    id: 'gem',
    color: '#A855F7',
    glow: 'rgba(168, 85, 247, 0.5)',
    maxCount: 10,
    category: 'collectible'
  },
  speedBoost: {
    id: 'speedBoost',
    color: '#06B6D4',
    glow: 'rgba(6, 182, 212, 0.5)',
    maxCount: 5,
    category: 'powerup'
  },
  shield: {
    id: 'shield',
    color: '#10B981',
    glow: 'rgba(16, 185, 129, 0.5)',
    maxCount: 3,
    category: 'powerup'
  }
};

// Rule triggers and actions for visual logic system
const RULE_TRIGGERS = {
  player_touches_coin: { 
    label: 'Hero touches Star', 
    icon: '⭐', 
    color: '#F59E0B',
    description: 'When the hero reaches a star'
  },
  player_touches_enemy: { 
    label: 'Hero touches Guardian', 
    icon: '👾', 
    color: '#EF4444',
    description: 'When the hero bumps into an enemy'
  },
  player_touches_goal: { 
    label: 'Hero reaches Portal', 
    icon: '🚪', 
    color: '#3B82F6',
    description: 'When the hero finds the exit'
  },
  score_reaches: { 
    label: 'Score reaches target', 
    icon: '🎯', 
    color: '#8B5CF6',
    description: 'When you collect enough stars'
  },
  all_coins_collected: { 
    label: 'All Stars collected', 
    icon: '✨', 
    color: '#FBBF24',
    description: 'When every star is collected'
  },
  // New triggers
  player_touches_heart: {
    label: 'Hero touches Heart',
    icon: '❤️',
    color: '#EC4899',
    description: 'When the hero finds a heart'
  },
  player_touches_key: {
    label: 'Hero touches Key',
    icon: '🔑',
    color: '#FBBF24',
    description: 'When the hero picks up a key'
  },
  player_touches_gem: {
    label: 'Hero touches Gem',
    icon: '💎',
    color: '#A855F7',
    description: 'When the hero finds a gem'
  },
  player_touches_speedBoost: {
    label: 'Hero touches Speed Boost',
    icon: '⚡',
    color: '#06B6D4',
    description: 'When the hero grabs a power-up'
  },
  player_touches_shield: {
    label: 'Hero touches Shield',
    icon: '🛡️',
    color: '#10B981',
    description: 'When the hero grabs a shield'
  },
  all_keys_collected: {
    label: 'All Keys collected',
    icon: '🗝️',
    color: '#FBBF24',
    description: 'When all keys are collected'
  }
};

const RULE_ACTIONS = {
  score_add: { 
    label: 'Add to score', 
    icon: '+', 
    color: '#22C55E',
    hasValue: true,
    defaultValue: 1
  },
  lose_life: { 
    label: 'Lose a life', 
    icon: '💔', 
    color: '#EF4444',
    hasValue: false
  },
  win_game: { 
    label: 'Win the game!', 
    icon: '🏆', 
    color: '#FBBF24',
    hasValue: false
  },
  reset_player: { 
    label: 'Reset player position', 
    icon: '↩️', 
    color: '#06B6D4',
    hasValue: false
  },
  // New actions
  gain_life: {
    label: 'Gain a life',
    icon: '❤️',
    color: '#EC4899',
    hasValue: false
  },
  unlock_portal: {
    label: 'Unlock the portal',
    icon: '🔓',
    color: '#FBBF24',
    hasValue: false
  },
  speed_boost: {
    label: 'Speed boost!',
    icon: '⚡',
    color: '#06B6D4',
    hasValue: false
  },
  bonus_score: {
    label: 'Bonus points',
    icon: '💎',
    color: '#A855F7',
    hasValue: true,
    defaultValue: 5
  },
  activate_shield: {
    label: 'Activate shield',
    icon: '🛡️',
    color: '#10B981',
    hasValue: false
  }
};

// Default rules (beginner-friendly)
const DEFAULT_RULES = [
  { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
  { id: 'hit-enemy', trigger: 'player_touches_enemy', action: 'lose_life', value: 1, enabled: true },
  { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true },
  { id: 'win-score', trigger: 'score_reaches', action: 'win_game', value: 5, enabled: false },
  // New default rules (disabled by default)
  { id: 'collect-heart', trigger: 'player_touches_heart', action: 'gain_life', value: 0, enabled: true },
  { id: 'collect-key', trigger: 'player_touches_key', action: 'unlock_portal', value: 0, enabled: false },
  { id: 'collect-gem', trigger: 'player_touches_gem', action: 'bonus_score', value: 5, enabled: true },
  { id: 'collect-speed', trigger: 'player_touches_speedBoost', action: 'speed_boost', value: 0, enabled: true },
  { id: 'collect-shield', trigger: 'player_touches_shield', action: 'activate_shield', value: 0, enabled: true }
];

// Template categories with progressive difficulty levels
const TEMPLATE_CATEGORIES = [
  {
    id: 'collect',
    name: 'Star Collector',
    description: 'Gather stars and treasures!',
    icon: '⭐',
    color: '#F59E0B',
    gameType: 'Collect',
    levels: [
      {
        id: 'collect-1',
        levelNum: 1,
        name: 'Beginner',
        subtitle: 'First Stars',
        description: 'Collect 3 stars with no obstacles',
        preview: '🏃 → ⭐⭐⭐',
        objects: [
          { type: 'player', x: 1, y: 5 },
          { type: 'coin', x: 5, y: 3 },
          { type: 'coin', x: 8, y: 7 },
          { type: 'coin', x: 12, y: 5 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'win-score', trigger: 'score_reaches', action: 'win_game', value: 3, enabled: true }
        ]
      },
      {
        id: 'collect-2',
        levelNum: 2,
        name: 'Intermediate',
        subtitle: 'Obstacle Course',
        description: 'Navigate around walls to collect 5 stars',
        preview: '🏃 → 🧱 → ⭐⭐⭐⭐⭐',
        objects: [
          { type: 'player', x: 1, y: 10 },
          { type: 'coin', x: 3, y: 3 },
          { type: 'coin', x: 7, y: 5 },
          { type: 'coin', x: 12, y: 2 },
          { type: 'coin', x: 10, y: 8 },
          { type: 'coin', x: 5, y: 7 },
          { type: 'wall', x: 5, y: 4 }, { type: 'wall', x: 5, y: 5 }, { type: 'wall', x: 5, y: 6 },
          { type: 'wall', x: 10, y: 4 }, { type: 'wall', x: 10, y: 5 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'win-score', trigger: 'score_reaches', action: 'win_game', value: 5, enabled: true }
        ]
      },
      {
        id: 'collect-3',
        levelNum: 3,
        name: 'Advanced',
        subtitle: 'Guardian Watch',
        description: 'Collect stars while avoiding guardians!',
        preview: '🏃 → 👾 → ⭐⭐⭐⭐⭐ → 💎',
        objects: [
          { type: 'player', x: 0, y: 5 },
          { type: 'coin', x: 3, y: 2 }, { type: 'coin', x: 6, y: 8 }, { type: 'coin', x: 9, y: 3 },
          { type: 'coin', x: 12, y: 7 }, { type: 'coin', x: 14, y: 5 },
          { type: 'gem', x: 8, y: 5 },
          { type: 'enemy', x: 4, y: 5 }, { type: 'enemy', x: 10, y: 5 },
          { type: 'heart', x: 7, y: 1 },
          { type: 'wall', x: 6, y: 4 }, { type: 'wall', x: 6, y: 5 }, { type: 'wall', x: 6, y: 6 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'collect-gem', trigger: 'player_touches_gem', action: 'bonus_score', value: 5, enabled: true },
          { id: 'hit-enemy', trigger: 'player_touches_enemy', action: 'lose_life', value: 1, enabled: true },
          { id: 'collect-heart', trigger: 'player_touches_heart', action: 'gain_life', value: 0, enabled: true },
          { id: 'win-score', trigger: 'score_reaches', action: 'win_game', value: 10, enabled: true }
        ]
      }
    ]
  },
  {
    id: 'maze',
    name: 'Maze Escape',
    description: 'Find your way through!',
    icon: '🚪',
    color: '#3B82F6',
    gameType: 'Maze',
    levels: [
      {
        id: 'maze-1',
        levelNum: 1,
        name: 'Beginner',
        subtitle: 'Simple Path',
        description: 'Navigate a simple maze to the portal',
        preview: '🏃 → 🧱 → 🚪',
        objects: [
          { type: 'player', x: 1, y: 5 },
          { type: 'goal', x: 14, y: 5 },
          { type: 'wall', x: 5, y: 3 }, { type: 'wall', x: 5, y: 4 }, { type: 'wall', x: 5, y: 5 },
          { type: 'wall', x: 5, y: 6 }, { type: 'wall', x: 5, y: 7 },
          { type: 'wall', x: 10, y: 3 }, { type: 'wall', x: 10, y: 4 }, { type: 'wall', x: 10, y: 5 },
          { type: 'wall', x: 10, y: 6 }, { type: 'wall', x: 10, y: 7 },
          { type: 'coin', x: 3, y: 2 }, { type: 'coin', x: 12, y: 8 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      },
      {
        id: 'maze-2',
        levelNum: 2,
        name: 'Intermediate',
        subtitle: 'Winding Path',
        description: 'A larger maze with more twists and turns',
        preview: '🏃 → 🧱🧱🧱 → 🚪',
        objects: [
          { type: 'player', x: 1, y: 10 },
          { type: 'goal', x: 14, y: 1 },
          { type: 'wall', x: 4, y: 0 }, { type: 'wall', x: 4, y: 1 }, { type: 'wall', x: 4, y: 2 }, 
          { type: 'wall', x: 4, y: 3 }, { type: 'wall', x: 4, y: 4 }, { type: 'wall', x: 4, y: 5 },
          { type: 'wall', x: 8, y: 6 }, { type: 'wall', x: 8, y: 7 }, { type: 'wall', x: 8, y: 8 }, 
          { type: 'wall', x: 8, y: 9 }, { type: 'wall', x: 8, y: 10 }, { type: 'wall', x: 8, y: 11 },
          { type: 'wall', x: 12, y: 0 }, { type: 'wall', x: 12, y: 1 }, { type: 'wall', x: 12, y: 2 }, 
          { type: 'wall', x: 12, y: 3 }, { type: 'wall', x: 12, y: 4 }, { type: 'wall', x: 12, y: 5 },
          { type: 'coin', x: 2, y: 5 }, { type: 'coin', x: 6, y: 3 }, { type: 'coin', x: 10, y: 8 },
          { type: 'gem', x: 6, y: 8 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'collect-gem', trigger: 'player_touches_gem', action: 'bonus_score', value: 5, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      },
      {
        id: 'maze-3',
        levelNum: 3,
        name: 'Advanced',
        subtitle: 'Locked Exit',
        description: 'Find the key to unlock the portal!',
        preview: '🏃 → 🔑 → 🧱🧱 → 🚪',
        objects: [
          { type: 'player', x: 0, y: 10 },
          { type: 'goal', x: 15, y: 1 },
          { type: 'key', x: 7, y: 10 },
          { type: 'wall', x: 3, y: 0 }, { type: 'wall', x: 3, y: 1 }, { type: 'wall', x: 3, y: 2 },
          { type: 'wall', x: 3, y: 3 }, { type: 'wall', x: 3, y: 4 }, { type: 'wall', x: 3, y: 5 },
          { type: 'wall', x: 3, y: 6 }, { type: 'wall', x: 3, y: 7 }, { type: 'wall', x: 3, y: 8 },
          { type: 'wall', x: 7, y: 3 }, { type: 'wall', x: 7, y: 4 }, { type: 'wall', x: 7, y: 5 },
          { type: 'wall', x: 7, y: 6 }, { type: 'wall', x: 7, y: 7 }, { type: 'wall', x: 7, y: 8 },
          { type: 'wall', x: 11, y: 4 }, { type: 'wall', x: 11, y: 5 }, { type: 'wall', x: 11, y: 6 },
          { type: 'wall', x: 11, y: 7 }, { type: 'wall', x: 11, y: 8 }, { type: 'wall', x: 11, y: 9 },
          { type: 'wall', x: 11, y: 10 }, { type: 'wall', x: 11, y: 11 },
          { type: 'coin', x: 1, y: 3 }, { type: 'coin', x: 5, y: 1 }, { type: 'coin', x: 9, y: 9 },
          { type: 'gem', x: 14, y: 5 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'collect-key', trigger: 'player_touches_key', action: 'unlock_portal', value: 0, enabled: true },
          { id: 'collect-gem', trigger: 'player_touches_gem', action: 'bonus_score', value: 5, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      }
    ]
  },
  {
    id: 'survival',
    name: 'Guardian Gauntlet',
    description: 'Survive the guardians!',
    icon: '👾',
    color: '#EF4444',
    gameType: 'Survival',
    levels: [
      {
        id: 'survival-1',
        levelNum: 1,
        name: 'Beginner',
        subtitle: 'Few Guardians',
        description: 'Dodge 2 guardians to reach the portal',
        preview: '🏃 → 👾👾 → 🚪',
        objects: [
          { type: 'player', x: 1, y: 5 },
          { type: 'goal', x: 14, y: 5 },
          { type: 'enemy', x: 6, y: 4 },
          { type: 'enemy', x: 10, y: 6 },
          { type: 'coin', x: 4, y: 2 }, { type: 'coin', x: 8, y: 8 }, { type: 'coin', x: 12, y: 3 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'hit-enemy', trigger: 'player_touches_enemy', action: 'lose_life', value: 1, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      },
      {
        id: 'survival-2',
        levelNum: 2,
        name: 'Intermediate',
        subtitle: 'Guardian Field',
        description: 'Navigate through 5 guardians with heart pickups',
        preview: '🏃 → 👾👾👾 → ❤️ → 🚪',
        objects: [
          { type: 'player', x: 1, y: 5 },
          { type: 'goal', x: 14, y: 5 },
          { type: 'enemy', x: 4, y: 3 },
          { type: 'enemy', x: 4, y: 7 },
          { type: 'enemy', x: 8, y: 5 },
          { type: 'enemy', x: 11, y: 2 },
          { type: 'enemy', x: 11, y: 8 },
          { type: 'coin', x: 6, y: 1 }, { type: 'coin', x: 6, y: 9 }, { type: 'coin', x: 13, y: 5 },
          { type: 'heart', x: 8, y: 1 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'hit-enemy', trigger: 'player_touches_enemy', action: 'lose_life', value: 1, enabled: true },
          { id: 'collect-heart', trigger: 'player_touches_heart', action: 'gain_life', value: 0, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      },
      {
        id: 'survival-3',
        levelNum: 3,
        name: 'Advanced',
        subtitle: 'Gauntlet Run',
        description: 'Dense guardian field with walls and power-ups!',
        preview: '🏃 → 👾👾👾👾 → ⚡❤️ → 🚪',
        objects: [
          { type: 'player', x: 0, y: 5 },
          { type: 'goal', x: 15, y: 5 },
          { type: 'enemy', x: 3, y: 3 }, { type: 'enemy', x: 3, y: 7 },
          { type: 'enemy', x: 6, y: 5 },
          { type: 'enemy', x: 9, y: 2 }, { type: 'enemy', x: 9, y: 8 },
          { type: 'enemy', x: 12, y: 4 }, { type: 'enemy', x: 12, y: 6 },
          { type: 'wall', x: 5, y: 4 }, { type: 'wall', x: 5, y: 5 }, { type: 'wall', x: 5, y: 6 },
          { type: 'wall', x: 10, y: 4 }, { type: 'wall', x: 10, y: 5 }, { type: 'wall', x: 10, y: 6 },
          { type: 'coin', x: 2, y: 1 }, { type: 'coin', x: 7, y: 9 }, { type: 'coin', x: 14, y: 2 },
          { type: 'heart', x: 7, y: 1 }, { type: 'heart', x: 11, y: 9 },
          { type: 'speedBoost', x: 4, y: 5 },
          { type: 'gem', x: 14, y: 5 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'hit-enemy', trigger: 'player_touches_enemy', action: 'lose_life', value: 1, enabled: true },
          { id: 'collect-heart', trigger: 'player_touches_heart', action: 'gain_life', value: 0, enabled: true },
          { id: 'collect-speed', trigger: 'player_touches_speedBoost', action: 'speed_boost', value: 0, enabled: true },
          { id: 'collect-gem', trigger: 'player_touches_gem', action: 'bonus_score', value: 5, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      }
    ]
  },
  {
    id: 'puzzle',
    name: 'Treasure Hunt',
    description: 'Solve puzzles to find treasure!',
    icon: '🔑',
    color: '#FBBF24',
    gameType: 'Puzzle',
    levels: [
      {
        id: 'puzzle-1',
        levelNum: 1,
        name: 'Beginner',
        subtitle: 'One Key',
        description: 'Find the key to unlock the treasure room',
        preview: '🏃 → 🔑 → 🚪',
        objects: [
          { type: 'player', x: 1, y: 5 },
          { type: 'goal', x: 14, y: 5 },
          { type: 'key', x: 7, y: 5 },
          { type: 'wall', x: 10, y: 3 }, { type: 'wall', x: 10, y: 4 }, { type: 'wall', x: 10, y: 5 },
          { type: 'wall', x: 10, y: 6 }, { type: 'wall', x: 10, y: 7 },
          { type: 'coin', x: 3, y: 2 }, { type: 'coin', x: 12, y: 8 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'collect-key', trigger: 'player_touches_key', action: 'unlock_portal', value: 0, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      },
      {
        id: 'puzzle-2',
        levelNum: 2,
        name: 'Intermediate',
        subtitle: 'Two Keys',
        description: 'Find both keys hidden in the maze',
        preview: '🏃 → 🔑🔑 → 🚪💎',
        objects: [
          { type: 'player', x: 1, y: 5 },
          { type: 'goal', x: 14, y: 5 },
          { type: 'key', x: 3, y: 2 },
          { type: 'key', x: 11, y: 9 },
          { type: 'gem', x: 13, y: 4 }, { type: 'gem', x: 13, y: 6 },
          { type: 'wall', x: 6, y: 0 }, { type: 'wall', x: 6, y: 1 }, { type: 'wall', x: 6, y: 2 },
          { type: 'wall', x: 6, y: 3 }, { type: 'wall', x: 6, y: 4 },
          { type: 'wall', x: 6, y: 7 }, { type: 'wall', x: 6, y: 8 }, { type: 'wall', x: 6, y: 9 },
          { type: 'wall', x: 6, y: 10 }, { type: 'wall', x: 6, y: 11 },
          { type: 'wall', x: 11, y: 3 }, { type: 'wall', x: 11, y: 4 }, { type: 'wall', x: 11, y: 5 },
          { type: 'wall', x: 11, y: 6 }, { type: 'wall', x: 11, y: 7 },
          { type: 'coin', x: 2, y: 8 }, { type: 'coin', x: 9, y: 1 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'collect-key', trigger: 'player_touches_key', action: 'unlock_portal', value: 0, enabled: true },
          { id: 'collect-gem', trigger: 'player_touches_gem', action: 'bonus_score', value: 5, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      },
      {
        id: 'puzzle-3',
        levelNum: 3,
        name: 'Advanced',
        subtitle: 'Guardian Vault',
        description: 'Collect 3 keys while avoiding guardians!',
        preview: '🏃 → 👾 → 🔑🔑🔑 → 🚪💎💎',
        objects: [
          { type: 'player', x: 0, y: 5 },
          { type: 'goal', x: 15, y: 5 },
          { type: 'key', x: 4, y: 1 }, { type: 'key', x: 8, y: 10 }, { type: 'key', x: 12, y: 3 },
          { type: 'gem', x: 14, y: 4 }, { type: 'gem', x: 14, y: 6 },
          { type: 'enemy', x: 5, y: 5 }, { type: 'enemy', x: 10, y: 5 },
          { type: 'wall', x: 3, y: 3 }, { type: 'wall', x: 3, y: 4 }, { type: 'wall', x: 3, y: 5 },
          { type: 'wall', x: 3, y: 6 }, { type: 'wall', x: 3, y: 7 },
          { type: 'wall', x: 7, y: 3 }, { type: 'wall', x: 7, y: 4 }, { type: 'wall', x: 7, y: 5 },
          { type: 'wall', x: 7, y: 6 }, { type: 'wall', x: 7, y: 7 },
          { type: 'wall', x: 13, y: 3 }, { type: 'wall', x: 13, y: 4 }, { type: 'wall', x: 13, y: 5 },
          { type: 'wall', x: 13, y: 6 }, { type: 'wall', x: 13, y: 7 },
          { type: 'heart', x: 6, y: 1 },
          { type: 'coin', x: 2, y: 9 }, { type: 'coin', x: 11, y: 1 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'collect-key', trigger: 'player_touches_key', action: 'unlock_portal', value: 0, enabled: true },
          { id: 'hit-enemy', trigger: 'player_touches_enemy', action: 'lose_life', value: 1, enabled: true },
          { id: 'collect-heart', trigger: 'player_touches_heart', action: 'gain_life', value: 0, enabled: true },
          { id: 'collect-gem', trigger: 'player_touches_gem', action: 'bonus_score', value: 5, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      }
    ]
  },
  {
    id: 'action',
    name: 'Speed Run',
    description: 'Race to the finish!',
    icon: '⚡',
    color: '#06B6D4',
    gameType: 'Action',
    levels: [
      {
        id: 'action-1',
        levelNum: 1,
        name: 'Beginner',
        subtitle: 'Quick Dash',
        description: 'Use speed boosts to reach the portal',
        preview: '🏃 → ⚡ → 🚪',
        objects: [
          { type: 'player', x: 1, y: 5 },
          { type: 'goal', x: 14, y: 5 },
          { type: 'speedBoost', x: 5, y: 5 },
          { type: 'speedBoost', x: 10, y: 5 },
          { type: 'coin', x: 3, y: 2 }, { type: 'coin', x: 8, y: 8 }, { type: 'coin', x: 12, y: 3 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'collect-speed', trigger: 'player_touches_speedBoost', action: 'speed_boost', value: 0, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      },
      {
        id: 'action-2',
        levelNum: 2,
        name: 'Intermediate',
        subtitle: 'Power Sprint',
        description: 'Dash past guardians with power-ups',
        preview: '🏃 → ⚡⚡ → 👾👾 → 🚪',
        objects: [
          { type: 'player', x: 0, y: 5 },
          { type: 'goal', x: 15, y: 5 },
          { type: 'speedBoost', x: 3, y: 5 },
          { type: 'speedBoost', x: 9, y: 5 },
          { type: 'enemy', x: 6, y: 4 }, { type: 'enemy', x: 6, y: 6 },
          { type: 'enemy', x: 12, y: 4 }, { type: 'enemy', x: 12, y: 6 },
          { type: 'coin', x: 2, y: 2 }, { type: 'coin', x: 8, y: 8 }, { type: 'coin', x: 14, y: 2 },
          { type: 'heart', x: 8, y: 2 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'hit-enemy', trigger: 'player_touches_enemy', action: 'lose_life', value: 1, enabled: true },
          { id: 'collect-heart', trigger: 'player_touches_heart', action: 'gain_life', value: 0, enabled: true },
          { id: 'collect-speed', trigger: 'player_touches_speedBoost', action: 'speed_boost', value: 0, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      },
      {
        id: 'action-3',
        levelNum: 3,
        name: 'Advanced',
        subtitle: 'Ultimate Rush',
        description: 'Dense obstacles, many power-ups, max challenge!',
        preview: '🏃 → ⚡⚡⚡ → 👾👾👾 → 💎 → 🚪',
        objects: [
          { type: 'player', x: 0, y: 5 },
          { type: 'goal', x: 15, y: 5 },
          { type: 'speedBoost', x: 2, y: 5 },
          { type: 'speedBoost', x: 7, y: 5 },
          { type: 'speedBoost', x: 12, y: 5 },
          { type: 'enemy', x: 4, y: 3 }, { type: 'enemy', x: 4, y: 7 },
          { type: 'enemy', x: 9, y: 4 }, { type: 'enemy', x: 9, y: 6 },
          { type: 'enemy', x: 14, y: 3 }, { type: 'enemy', x: 14, y: 7 },
          { type: 'wall', x: 5, y: 4 }, { type: 'wall', x: 5, y: 5 }, { type: 'wall', x: 5, y: 6 },
          { type: 'wall', x: 10, y: 4 }, { type: 'wall', x: 10, y: 5 }, { type: 'wall', x: 10, y: 6 },
          { type: 'coin', x: 1, y: 1 }, { type: 'coin', x: 6, y: 9 }, { type: 'coin', x: 11, y: 1 },
          { type: 'heart', x: 6, y: 1 }, { type: 'heart', x: 11, y: 9 },
          { type: 'gem', x: 14, y: 5 }
        ],
        rules: [
          { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
          { id: 'hit-enemy', trigger: 'player_touches_enemy', action: 'lose_life', value: 1, enabled: true },
          { id: 'collect-heart', trigger: 'player_touches_heart', action: 'gain_life', value: 0, enabled: true },
          { id: 'collect-speed', trigger: 'player_touches_speedBoost', action: 'speed_boost', value: 0, enabled: true },
          { id: 'collect-gem', trigger: 'player_touches_gem', action: 'bonus_score', value: 5, enabled: true },
          { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
        ]
      }
    ]
  }
];

// Flatten templates for backwards compatibility (used in loadTemplate)
const STARTER_TEMPLATES = TEMPLATE_CATEGORIES.flatMap(cat => 
  cat.levels.map(level => ({
    ...level,
    icon: cat.icon,
    color: cat.color,
    gameType: cat.gameType,
    categoryId: cat.id,
    categoryName: cat.name
  }))
);

// ============ FEATURED SHOWCASE GAME ============
// A polished demo game that demonstrates advanced mechanics
const FEATURED_SHOWCASE_GAME = {
  id: 'temple-escape-crystal-run',
  name: 'Temple Escape: Crystal Run',
  description: 'Navigate through ancient temple chambers, avoid patrolling guardians, collect magical crystals, and find the keys to unlock the sacred portal!',
  author: 'Temple Architects',
  difficulty: 'Expert',
  icon: '🏆',
  color: '#8B5CF6',
  features: [
    'Moving Guardians',
    'Multiple Keys',
    'Power-ups',
    'Maze Sections',
    'Bonus Gems'
  ],
  // Enemy patrol patterns: { enemyIndex: { axis: 'x'|'y', min, max, speed } }
  enemyPatrols: {
    0: { axis: 'y', min: 2, max: 6, speed: 800 },    // Vertical patrol
    1: { axis: 'x', min: 3, max: 7, speed: 1000 },   // Horizontal patrol
    2: { axis: 'y', min: 5, max: 9, speed: 700 },    // Fast vertical
    3: { axis: 'x', min: 9, max: 13, speed: 900 },   // Horizontal right side
    4: { axis: 'y', min: 1, max: 5, speed: 1100 }    // Slow vertical
  },
  objects: [
    // Player start
    { type: 'player', x: 0, y: 5 },
    // Goal (locked portal)
    { type: 'goal', x: 15, y: 5 },
    // Keys (3 to collect)
    { type: 'key', x: 4, y: 1 },
    { type: 'key', x: 8, y: 10 },
    { type: 'key', x: 13, y: 2 },
    // Moving guardians (5 with patrol patterns)
    { type: 'enemy', x: 5, y: 4 },   // Patrol 0
    { type: 'enemy', x: 5, y: 8 },   // Patrol 1
    { type: 'enemy', x: 11, y: 7 },  // Patrol 2
    { type: 'enemy', x: 11, y: 3 },  // Patrol 3
    { type: 'enemy', x: 8, y: 3 },   // Patrol 4
    // Maze walls - Chamber 1
    { type: 'wall', x: 3, y: 0 }, { type: 'wall', x: 3, y: 1 }, { type: 'wall', x: 3, y: 2 },
    { type: 'wall', x: 3, y: 4 }, { type: 'wall', x: 3, y: 5 }, { type: 'wall', x: 3, y: 6 },
    { type: 'wall', x: 3, y: 8 }, { type: 'wall', x: 3, y: 9 }, { type: 'wall', x: 3, y: 10 }, { type: 'wall', x: 3, y: 11 },
    // Maze walls - Chamber 2
    { type: 'wall', x: 7, y: 0 }, { type: 'wall', x: 7, y: 1 },
    { type: 'wall', x: 7, y: 5 }, { type: 'wall', x: 7, y: 6 }, { type: 'wall', x: 7, y: 7 },
    { type: 'wall', x: 7, y: 9 }, { type: 'wall', x: 7, y: 10 }, { type: 'wall', x: 7, y: 11 },
    // Maze walls - Chamber 3
    { type: 'wall', x: 10, y: 0 }, { type: 'wall', x: 10, y: 1 }, { type: 'wall', x: 10, y: 2 },
    { type: 'wall', x: 10, y: 4 }, { type: 'wall', x: 10, y: 5 },
    { type: 'wall', x: 10, y: 9 }, { type: 'wall', x: 10, y: 10 }, { type: 'wall', x: 10, y: 11 },
    // Final chamber entrance
    { type: 'wall', x: 13, y: 0 }, { type: 'wall', x: 13, y: 4 },
    { type: 'wall', x: 13, y: 6 }, { type: 'wall', x: 13, y: 7 }, { type: 'wall', x: 13, y: 8 },
    { type: 'wall', x: 13, y: 10 }, { type: 'wall', x: 13, y: 11 },
    // Collectibles - Stars in each chamber
    { type: 'coin', x: 1, y: 2 }, { type: 'coin', x: 1, y: 8 },
    { type: 'coin', x: 5, y: 1 }, { type: 'coin', x: 5, y: 10 },
    { type: 'coin', x: 9, y: 5 },
    { type: 'coin', x: 12, y: 1 }, { type: 'coin', x: 12, y: 10 },
    { type: 'coin', x: 14, y: 3 }, { type: 'coin', x: 14, y: 7 },
    // Bonus gems (hidden in tricky spots)
    { type: 'gem', x: 2, y: 0 },
    { type: 'gem', x: 6, y: 8 },
    { type: 'gem', x: 9, y: 11 },
    { type: 'gem', x: 14, y: 5 },
    // Power-ups
    { type: 'heart', x: 1, y: 5 },
    { type: 'heart', x: 11, y: 0 },
    { type: 'speedBoost', x: 6, y: 3 },
    { type: 'speedBoost', x: 9, y: 8 },
    { type: 'shield', x: 4, y: 5 },
    { type: 'shield', x: 12, y: 5 }
  ],
  rules: [
    { id: 'collect-coin', trigger: 'player_touches_coin', action: 'score_add', value: 1, enabled: true },
    { id: 'collect-gem', trigger: 'player_touches_gem', action: 'bonus_score', value: 5, enabled: true },
    { id: 'hit-enemy', trigger: 'player_touches_enemy', action: 'lose_life', value: 1, enabled: true },
    { id: 'collect-heart', trigger: 'player_touches_heart', action: 'gain_life', value: 0, enabled: true },
    { id: 'collect-key', trigger: 'player_touches_key', action: 'unlock_portal', value: 0, enabled: true },
    { id: 'collect-speed', trigger: 'player_touches_speedBoost', action: 'speed_boost', value: 0, enabled: true },
    { id: 'collect-shield', trigger: 'player_touches_shield', action: 'activate_shield', value: 0, enabled: true },
    { id: 'reach-goal', trigger: 'player_touches_goal', action: 'win_game', value: 0, enabled: true }
  ]
};

// ============ VISUAL LOGIC BLOCK COMPONENT ============
const LogicBlock = ({ rule, onToggle, onUpdateValue, onRemove, isNew = false }) => {
  const trigger = RULE_TRIGGERS[rule.trigger];
  const action = RULE_ACTIONS[rule.action];
  
  if (!trigger || !action) return null;
  
  return (
    <div 
      className={`relative group rounded-xl border-2 transition-all duration-300 overflow-hidden ${
        rule.enabled 
          ? 'border-green-500/50 bg-gradient-to-r from-slate-800/80 to-slate-900/80' 
          : 'border-slate-700/30 bg-slate-900/50 opacity-60'
      } ${isNew ? 'animate-pulse ring-2 ring-purple-500' : ''}`}
    >
      {/* Toggle button */}
      <button
        onClick={() => onToggle(rule.id)}
        className={`absolute top-2 right-2 p-1.5 rounded-lg transition-all ${
          rule.enabled 
            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
            : 'bg-slate-700/30 text-slate-500 hover:bg-slate-700/50'
        }`}
        data-testid={`rule-toggle-${rule.id}`}
      >
        {rule.enabled ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
      </button>
      
      <div className="p-4">
        {/* IF section */}
        <div className="flex items-center gap-3 mb-3">
          <span className="px-2 py-1 rounded-lg bg-purple-500/20 text-purple-400 text-xs font-bold uppercase">
            IF
          </span>
          <div 
            className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1"
            style={{ backgroundColor: `${trigger.color}15`, border: `1px solid ${trigger.color}30` }}
          >
            <span className="text-xl">{trigger.icon}</span>
            <span className="text-white text-sm font-medium">{trigger.label}</span>
          </div>
        </div>
        
        {/* Value input for score_reaches */}
        {rule.trigger === 'score_reaches' && rule.enabled && (
          <div className="flex items-center gap-2 mb-3 ml-12">
            <span className="text-slate-400 text-sm">Target:</span>
            <input
              type="number"
              value={rule.value}
              onChange={(e) => onUpdateValue(rule.id, Math.max(1, parseInt(e.target.value) || 1))}
              className="w-16 px-2 py-1 text-sm bg-slate-800 border border-slate-700 rounded-lg text-white text-center"
              min="1"
              max="100"
              data-testid={`rule-value-${rule.id}`}
            />
          </div>
        )}
        
        {/* THEN section */}
        <div className="flex items-center gap-3">
          <span className="px-2 py-1 rounded-lg bg-cyan-500/20 text-cyan-400 text-xs font-bold uppercase">
            THEN
          </span>
          <div 
            className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1"
            style={{ backgroundColor: `${action.color}15`, border: `1px solid ${action.color}30` }}
          >
            <span className="text-xl">{action.icon}</span>
            <span className="text-white text-sm font-medium">{action.label}</span>
            {action.hasValue && rule.enabled && (
              <span className="ml-auto px-2 py-0.5 rounded bg-slate-800 text-amber-400 font-bold">
                +{rule.value}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Description tooltip */}
      <div className="px-4 pb-3">
        <p className="text-xs text-slate-500 italic">{trigger.description}</p>
      </div>
    </div>
  );
};

// ============ RULE BUILDER COMPONENT ============
const RuleBuilder = ({ onAdd, existingRules }) => {
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [ruleValue, setRuleValue] = useState(1);
  
  const handleCreate = () => {
    if (!selectedTrigger || !selectedAction) return;
    
    const newRule = {
      id: `custom-${Date.now()}`,
      trigger: selectedTrigger,
      action: selectedAction,
      value: ruleValue,
      enabled: true
    };
    
    onAdd(newRule);
    setShowBuilder(false);
    setSelectedTrigger(null);
    setSelectedAction(null);
    setRuleValue(1);
    toast.success('New rule created!');
  };
  
  if (!showBuilder) {
    return (
      <button
        onClick={() => setShowBuilder(true)}
        className="w-full p-4 rounded-xl border-2 border-dashed border-slate-700 hover:border-purple-500/50 transition-all flex items-center justify-center gap-2 text-slate-400 hover:text-purple-400"
        data-testid="add-rule-btn"
      >
        <Plus className="w-5 h-5" />
        <span>Create New Rule</span>
      </button>
    );
  }
  
  return (
    <div className="p-4 rounded-xl bg-slate-800/50 border border-purple-500/30 space-y-4">
      <h4 className="text-white font-semibold flex items-center gap-2">
        <Wand2 className="w-4 h-4 text-purple-400" />
        Create a Rule
      </h4>
      
      {/* Trigger Selection */}
      <div>
        <p className="text-sm text-slate-400 mb-2">When this happens:</p>
        <div className="grid grid-cols-1 gap-2">
          {Object.entries(RULE_TRIGGERS).map(([key, trigger]) => {
            const isUsed = existingRules.some(r => r.trigger === key && r.enabled);
            return (
              <button
                key={key}
                onClick={() => !isUsed && setSelectedTrigger(key)}
                disabled={isUsed}
                className={`p-2 rounded-lg text-left flex items-center gap-2 transition-all ${
                  selectedTrigger === key 
                    ? 'bg-purple-500/30 border-2 border-purple-500' 
                    : isUsed 
                      ? 'bg-slate-800/30 opacity-40 cursor-not-allowed'
                      : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
                }`}
              >
                <span className="text-lg">{trigger.icon}</span>
                <span className="text-sm text-white">{trigger.label}</span>
                {isUsed && <span className="ml-auto text-xs text-slate-500">(in use)</span>}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Action Selection */}
      {selectedTrigger && (
        <div>
          <p className="text-sm text-slate-400 mb-2">Do this:</p>
          <div className="grid grid-cols-1 gap-2">
            {Object.entries(RULE_ACTIONS).map(([key, action]) => (
              <button
                key={key}
                onClick={() => setSelectedAction(key)}
                className={`p-2 rounded-lg text-left flex items-center gap-2 transition-all ${
                  selectedAction === key 
                    ? 'bg-cyan-500/30 border-2 border-cyan-500' 
                    : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
                }`}
              >
                <span className="text-lg">{action.icon}</span>
                <span className="text-sm text-white">{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Value input if needed */}
      {selectedAction && RULE_ACTIONS[selectedAction].hasValue && (
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400">Amount:</span>
          <input
            type="number"
            value={ruleValue}
            onChange={(e) => setRuleValue(Math.max(1, parseInt(e.target.value) || 1))}
            className="w-20 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-center"
            min="1"
            max="10"
          />
        </div>
      )}
      
      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => setShowBuilder(false)}
          className="flex-1 py-2 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleCreate}
          disabled={!selectedTrigger || !selectedAction}
          className="flex-1 py-2 rounded-lg bg-purple-500 text-white font-semibold hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="create-rule-confirm"
        >
          Create Rule
        </button>
      </div>
    </div>
  );
};

// ============ MOBILE CONTROLS COMPONENT ============
// Enhanced joystick-style controls with visual feedback
const MobileControls = ({ onMove, disabled, isTouchDevice }) => {
  // Track if we just handled a touch to prevent double-firing
  const touchHandledRef = useRef(false);
  const [activeDirection, setActiveDirection] = useState(null);
  
  const handleMove = (direction, isTouch = false) => {
    if (disabled) return;
    
    // Visual feedback
    setActiveDirection(direction);
    setTimeout(() => setActiveDirection(null), 150);
    
    // If this is a touch event, mark it and proceed
    if (isTouch) {
      touchHandledRef.current = true;
      onMove(direction);
      // Reset after a short delay to allow future clicks (for mouse users)
      setTimeout(() => { touchHandledRef.current = false; }, 300);
      return;
    }
    
    // If this is a click and we just handled a touch, ignore it
    if (touchHandledRef.current) {
      return;
    }
    
    onMove(direction);
  };
  
  // Show on touch devices OR on screen widths <= 1024px (tablet/mobile)
  // This ensures iPad (768px-1024px) always shows controls
  const [showControls, setShowControls] = useState(false);
  
  useEffect(() => {
    const checkShowControls = () => {
      const isTouch = 
        'ontouchstart' in window || 
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(pointer: coarse)').matches;
      const isTabletOrSmaller = window.innerWidth <= 1024;
      setShowControls(isTouch || isTabletOrSmaller);
    };
    
    checkShowControls();
    window.addEventListener('resize', checkShowControls);
    return () => window.removeEventListener('resize', checkShowControls);
  }, []);
  
  if (!showControls && !isTouchDevice) return null;
  
  // Joystick-style control layout
  return (
    <div className="flex items-center justify-center gap-8 mt-4 mb-8 px-4" data-testid="touch-controls">
      {/* D-Pad / Joystick style controls */}
      <div className="relative">
        {/* Outer ring for joystick feel */}
        <div className="w-44 h-44 rounded-full bg-slate-900/80 border-4 border-slate-700 flex items-center justify-center shadow-2xl">
          {/* Inner control area */}
          <div className="relative w-36 h-36">
            {/* Center indicator */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-slate-800 border-2 border-slate-600" />
            
            {/* Up button */}
            <button
              onClick={() => handleMove('up', false)}
              onTouchStart={(e) => { e.preventDefault(); handleMove('up', true); }}
              className={`absolute top-0 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                disabled 
                  ? 'bg-slate-800/40 border border-slate-700/50' 
                  : activeDirection === 'up'
                    ? 'bg-purple-400 scale-95 shadow-lg shadow-purple-500/50'
                    : 'bg-gradient-to-b from-purple-500 to-purple-700 border-2 border-purple-400 active:scale-90'
              }`}
              data-testid="touch-up"
            >
              <ArrowUp className={`w-8 h-8 ${disabled ? 'text-slate-600' : 'text-white drop-shadow-md'}`} />
            </button>
            
            {/* Down button */}
            <button
              onClick={() => handleMove('down', false)}
              onTouchStart={(e) => { e.preventDefault(); handleMove('down', true); }}
              className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                disabled 
                  ? 'bg-slate-800/40 border border-slate-700/50' 
                  : activeDirection === 'down'
                    ? 'bg-purple-400 scale-95 shadow-lg shadow-purple-500/50'
                    : 'bg-gradient-to-t from-purple-500 to-purple-700 border-2 border-purple-400 active:scale-90'
              }`}
              data-testid="touch-down"
            >
              <ArrowDown className={`w-8 h-8 ${disabled ? 'text-slate-600' : 'text-white drop-shadow-md'}`} />
            </button>
            
            {/* Left button */}
            <button
              onClick={() => handleMove('left', false)}
              onTouchStart={(e) => { e.preventDefault(); handleMove('left', true); }}
              className={`absolute left-0 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                disabled 
                  ? 'bg-slate-800/40 border border-slate-700/50' 
                  : activeDirection === 'left'
                    ? 'bg-purple-400 scale-95 shadow-lg shadow-purple-500/50'
                    : 'bg-gradient-to-r from-purple-500 to-purple-700 border-2 border-purple-400 active:scale-90'
              }`}
              data-testid="touch-left"
            >
              <ArrowLeftIcon className={`w-8 h-8 ${disabled ? 'text-slate-600' : 'text-white drop-shadow-md'}`} />
            </button>
            
            {/* Right button */}
            <button
              onClick={() => handleMove('right', false)}
              onTouchStart={(e) => { e.preventDefault(); handleMove('right', true); }}
              className={`absolute right-0 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                disabled 
                  ? 'bg-slate-800/40 border border-slate-700/50' 
                  : activeDirection === 'right'
                    ? 'bg-purple-400 scale-95 shadow-lg shadow-purple-500/50'
                    : 'bg-gradient-to-l from-purple-500 to-purple-700 border-2 border-purple-400 active:scale-90'
              }`}
              data-testid="touch-right"
            >
              <ArrowRightIcon className={`w-8 h-8 ${disabled ? 'text-slate-600' : 'text-white drop-shadow-md'}`} />
            </button>
          </div>
        </div>
        
        {/* Label below joystick */}
        <p className="text-center text-slate-500 text-xs font-medium mt-2 uppercase tracking-wider">Move</p>
      </div>
    </div>
  );
};

// ============ GAME FORGE COMPONENT ============
const GameForge = ({ onBack, selectedChild, onAwardSparks, initialChallenge = null }) => {
  // View state
  const [activeTab, setActiveTab] = useState('build'); // build, rules, test, save
  const [showTemplates, setShowTemplates] = useState(!initialChallenge); // Skip templates if loading a challenge
  const [showHelp, setShowHelp] = useState(false);
  const [showAssetPacks, setShowAssetPacks] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showObjectGuide, setShowObjectGuide] = useState(false); // NEW: Object guide tooltip
  const [showOnboarding, setShowOnboarding] = useState(!initialChallenge); // Skip onboarding for challenges
  const [hoveredObject, setHoveredObject] = useState(null); // NEW: For hover tooltips
  const [containerWidth, setContainerWidth] = useState(0); // For responsive sizing
  
  // Asset pack state (for future expansion)
  const [currentAssetPack, setCurrentAssetPack] = useState('default');
  
  // Game design state
  const [gameName, setGameName] = useState('My Game');
  const [objects, setObjects] = useState([]);
  const [rules, setRules] = useState([...DEFAULT_RULES]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [draggedType, setDraggedType] = useState(null);
  
  // Game play state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [playerPos, setPlayerPos] = useState({ x: 0, y: 0 });
  const [gameObjects, setGameObjects] = useState([]);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameStatus, setGameStatus] = useState('playing'); // playing, won, lost
  const [sparksAwarded, setSparksAwarded] = useState(false);
  // New game state for power-ups
  const [hasKey, setHasKey] = useState(false);
  const [portalLocked, setPortalLocked] = useState(false);
  const [speedBoostActive, setSpeedBoostActive] = useState(false);
  const [speedBoostTimer, setSpeedBoostTimer] = useState(null);
  const [keysCollected, setKeysCollected] = useState(0);
  const [totalKeys, setTotalKeys] = useState(0);
  // Shield state
  const [shieldActive, setShieldActive] = useState(false);
  // Showcase mode state
  const [showShowcase, setShowShowcase] = useState(false);
  const [isShowcaseGame, setIsShowcaseGame] = useState(false);
  const [shouldStartAfterLoad, setShouldStartAfterLoad] = useState(false);
  // Enemy patrol state
  const [enemyPatrols, setEnemyPatrols] = useState(null);
  const enemyIntervalRef = useRef(null);
  
  // Saved games state
  const [savedGames, setSavedGames] = useState([]);
  
  // Challenge system state
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [shareCode, setShareCode] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [currentGameId, setCurrentGameId] = useState(null);
  const [showEnterChallengeModal, setShowEnterChallengeModal] = useState(false);
  const [challengeCodeInput, setChallengeCodeInput] = useState('');
  const [loadingChallenge, setLoadingChallenge] = useState(false);
  const [isPlayingChallenge, setIsPlayingChallenge] = useState(false);
  const [challengeCreator, setChallengeCreator] = useState(null);
  
  // Visual feedback state for "feel alive" animations
  const [recentlyPlaced, setRecentlyPlaced] = useState(null); // {x, y} of recently placed object
  const [recentlyRemoved, setRecentlyRemoved] = useState(null); // {x, y} of recently removed object
  const [saveAnimation, setSaveAnimation] = useState(null); // 'new' | 'updated' | null
  const [playModeCountdown, setPlayModeCountdown] = useState(null); // 3, 2, 1, 'GO!' or null
  const [showConfetti, setShowConfetti] = useState(false); // Win celebration
  const [loadingGames, setLoadingGames] = useState(false);
  
  // Touch device detection for showing on-screen controls
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  
  // Detect touch device on mount
  useEffect(() => {
    const checkTouch = () => {
      const isTouch = 
        'ontouchstart' in window || 
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(pointer: coarse)').matches;
      setIsTouchDevice(isTouch);
    };
    checkTouch();
    // Re-check on resize (e.g., connecting external keyboard)
    window.addEventListener('resize', checkTouch);
    return () => window.removeEventListener('resize', checkTouch);
  }, []);
  
  // Refs
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const audioContextRef = useRef(null);
  
  // Get asset pack data
  const assetPack = getAssetPack(currentAssetPack);
  
  // Calculate responsive cell size
  const cellSize = useMemo(() => getResponsiveCellSize(containerWidth), [containerWidth]);
  
  // Track container width for responsive sizing
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [showTemplates]);
  
  // Load saved games
  useEffect(() => {
    if (selectedChild?.id && activeTab === 'save') {
      loadSavedGames();
    }
  }, [selectedChild?.id, activeTab]);

  // Load initial challenge if provided
  useEffect(() => {
    if (initialChallenge) {
      setGameName(initialChallenge.name);
      setObjects(initialChallenge.objects.map((obj, idx) => ({
        ...obj,
        id: obj.id || `obj-${idx}`
      })));
      setRules(initialChallenge.rules || [...DEFAULT_RULES]);
      setChallengeCreator(initialChallenge.creator_name);
      setIsPlayingChallenge(true);
      setShowTemplates(false);
      setShowOnboarding(false);
      setActiveTab('build');
      
      // Use the existing pattern to auto-start after objects are loaded
      setShouldStartAfterLoad(true);
    }
  }, [initialChallenge]);

  
  const loadSavedGames = async () => {
    if (!selectedChild?.id) return;
    setLoadingGames(true);
    try {
      const games = await api.get(`/games?child_id=${selectedChild.id}`);
      setSavedGames(games.data || []);
    } catch (err) {
      console.error('Failed to load games:', err);
    } finally {
      setLoadingGames(false);
    }
  };
  
  // ============ TEMPLATE LOADING ============
  const loadTemplate = (template) => {
    setGameName(template.name);
    setObjects(template.objects.map((obj, i) => ({ ...obj, id: `obj-${i}` })));
    setRules(template.rules.length > 0 ? [...template.rules] : [...DEFAULT_RULES]);
    setShowTemplates(false);
    toast.success(`Loaded "${template.name}" template!`);
  };
  
  // Load a saved game
  const loadSavedGame = async (game) => {
    setGameName(game.name);
    setObjects(game.objects.map((obj, i) => ({ ...obj, id: obj.id || `obj-${i}` })));
    setRules(game.rules || [...DEFAULT_RULES]);
    setShowTemplates(false);
    setActiveTab('build');
    toast.success(`Loaded "${game.name}"!`);
  };
  
  // ============ OBJECT MANAGEMENT ============
  const handleDragStart = (type) => {
    setDraggedType(type);
  };
  
  const handleDragEnd = () => {
    setDraggedType(null);
  };
  
  const handleCanvasDrop = (e) => {
    if (!draggedType) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / CELL_SIZE);
    const y = Math.floor((e.clientY - rect.top) / CELL_SIZE);
    
    addObject(draggedType, x, y);
    setDraggedType(null);
  };
  
  const addObject = (type, x, y) => {
    // Validate position
    if (x < 0 || x >= GRID_WIDTH || y < 0 || y >= GRID_HEIGHT) return;
    
    // Check max count
    const typeInfo = OBJECT_TYPES[type];
    const currentCount = objects.filter(o => o.type === type).length;
    if (currentCount >= typeInfo.maxCount) {
      const assetInfo = assetPack.objects[type];
      toast.error(`Maximum ${typeInfo.maxCount} ${assetInfo.name}(s) allowed`);
      return;
    }
    
    // Check if cell is occupied
    const existing = objects.find(o => o.x === x && o.y === y);
    if (existing) {
      toast.error('Cell already occupied');
      return;
    }
    
    // Add object
    const newObj = {
      id: `obj-${Date.now()}`,
      type,
      x,
      y
    };
    setObjects(prev => [...prev, newObj]);
    playSound('place');
    
    // Trigger placement animation
    setRecentlyPlaced({ x, y });
    setTimeout(() => setRecentlyPlaced(null), 400);
    
    // Show mini feedback toast
    const assetInfo = assetPack.objects[type];
    toast.success(`${assetInfo?.emoji} Placed!`, { duration: 1000 });
  };
  
  const handleCellClick = (x, y) => {
    if (activeTab !== 'build' || isPlaying) return;
    
    const existing = objects.find(o => o.x === x && o.y === y);
    
    // Eraser mode - remove objects to create empty spaces
    if (draggedType === 'eraser') {
      if (existing) {
        // Trigger removal animation first
        setRecentlyRemoved({ x, y });
        
        // Small delay for animation to play, then remove
        setTimeout(() => {
          setObjects(prev => prev.filter(o => o.id !== existing.id));
          setRecentlyRemoved(null);
        }, 200);
        
        playSound('delete');
        toast.success('✨ Poof! Empty space created!', { duration: 1500 });
      }
      return;
    }
    
    if (existing) {
      setSelectedObject(existing);
    } else if (draggedType) {
      addObject(draggedType, x, y);
      setDraggedType(null);
    } else {
      setSelectedObject(null);
    }
  };
  
  const deleteSelectedObject = () => {
    if (!selectedObject) return;
    setObjects(prev => prev.filter(o => o.id !== selectedObject.id));
    setSelectedObject(null);
    playSound('delete');
  };
  
  const clearAllObjects = () => {
    if (objects.length === 0) return;
    setObjects([]);
    setSelectedObject(null);
    setDraggedType(null);
    toast.success('Board cleared!');
    playSound('delete');
  };
  
  // ============ RULES MANAGEMENT ============
  const toggleRule = (ruleId) => {
    setRules(prev => prev.map(r => 
      r.id === ruleId ? { ...r, enabled: !r.enabled } : r
    ));
  };
  
  const updateRuleValue = (ruleId, value) => {
    setRules(prev => prev.map(r => 
      r.id === ruleId ? { ...r, value } : r
    ));
  };
  
  const addRule = (newRule) => {
    setRules(prev => [...prev, newRule]);
  };
  
  const removeRule = (ruleId) => {
    setRules(prev => prev.filter(r => r.id !== ruleId));
  };
  
  // ============ SOUND EFFECTS ============
  const playSound = (type) => {
    if (!soundEnabled) return;
    
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      const sounds = {
        collect: { freq: [880, 1100], duration: 0.15 },
        hit: { freq: [200, 100], duration: 0.2 },
        win: { freq: [523, 659, 784], duration: 0.4 },
        lose: { freq: [300, 200, 150], duration: 0.4 },
        place: { freq: [440, 550], duration: 0.1 },
        delete: { freq: [300, 200], duration: 0.1 }
      };
      
      const sound = sounds[type] || sounds.place;
      const duration = sound.duration;
      
      sound.freq.forEach((f, i) => {
        osc.frequency.setValueAtTime(f, ctx.currentTime + (i * duration / sound.freq.length));
      });
      
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch (e) {
      // Audio not available
    }
  };
  
  // ============ GAME ENGINE ============
  const startGame = useCallback(() => {
    // Find player
    const player = objects.find(o => o.type === 'player');
    if (!player) {
      toast.error(`Add a ${assetPack.objects.player.name} to your game first!`);
      return;
    }
    
    // Count keys to determine if portal should be locked
    const keyCount = objects.filter(o => o.type === 'key').length;
    const hasKeyRule = rules.some(r => r.trigger === 'player_touches_key' && r.action === 'unlock_portal' && r.enabled);
    
    // Initialize game objects with patrol info
    const initialObjects = objects.filter(o => o.type !== 'player').map((o, idx) => {
      const obj = { ...o, active: true };
      // If showcase game with enemy patrols, add patrol data
      if (o.type === 'enemy' && isShowcaseGame && enemyPatrols) {
        const enemyIndex = objects.filter(ob => ob.type === 'enemy').indexOf(o);
        const patrol = enemyPatrols[enemyIndex];
        if (patrol) {
          obj.patrol = { ...patrol, direction: 1, originalX: o.x, originalY: o.y };
        }
      }
      return obj;
    });
    
    // Start countdown sequence
    setPlayModeCountdown(3);
    setActiveTab('test');
    playSound('place'); // Use place sound for countdown tick
    
    setTimeout(() => {
      setPlayModeCountdown(2);
      playSound('place');
    }, 600);
    
    setTimeout(() => {
      setPlayModeCountdown(1);
      playSound('place');
    }, 1200);
    
    setTimeout(() => {
      setPlayModeCountdown('GO!');
      playSound('achievement'); // Exciting sound for GO!
    }, 1800);
    
    setTimeout(() => {
      // Actually start the game after countdown
      setPlayModeCountdown(null);
      setPlayerPos({ x: player.x, y: player.y });
      setGameObjects(initialObjects);
      setScore(0);
      setLives(3);
      setGameStatus('playing');
      setIsPlaying(true);
      setIsPaused(false);
      setSparksAwarded(false);
      setShowConfetti(false);
      // Initialize power-up states
      setHasKey(false);
      setKeysCollected(0);
      setTotalKeys(keyCount);
      setPortalLocked(hasKeyRule && keyCount > 0);
      setSpeedBoostActive(false);
      setShieldActive(false);
      if (speedBoostTimer) clearTimeout(speedBoostTimer);
      setSpeedBoostTimer(null);
    }, 2200);
    
  }, [objects, assetPack, rules, speedBoostTimer, isShowcaseGame, enemyPatrols]);
  
  const stopGame = () => {
    setIsPlaying(false);
    setIsPaused(false);
    setSpeedBoostActive(false);
    setShieldActive(false);
    if (speedBoostTimer) clearTimeout(speedBoostTimer);
    if (enemyIntervalRef.current) clearInterval(enemyIntervalRef.current);
    setActiveTab('build');
  };
  
  // Effect to start game after objects are loaded (for showcase play)
  useEffect(() => {
    if (shouldStartAfterLoad && objects.length > 0 && objects.some(o => o.type === 'player')) {
      setShouldStartAfterLoad(false);
      // Small delay to ensure state is fully updated
      setTimeout(() => {
        startGame();
      }, 100);
    }
  }, [shouldStartAfterLoad, objects, startGame]);
  
  const togglePause = () => {
    setIsPaused(prev => !prev);
  };
  
  // Move player function
  const movePlayer = useCallback((direction) => {
    if (!isPlaying || isPaused || gameStatus !== 'playing') return;
    
    let newX = playerPos.x;
    let newY = playerPos.y;
    
    switch (direction) {
      case 'up':
        newY = Math.max(0, playerPos.y - 1);
        break;
      case 'down':
        newY = Math.min(GRID_HEIGHT - 1, playerPos.y + 1);
        break;
      case 'left':
        newX = Math.max(0, playerPos.x - 1);
        break;
      case 'right':
        newX = Math.min(GRID_WIDTH - 1, playerPos.x + 1);
        break;
      default:
        return;
    }
    
    // Check wall collision
    const hitWall = gameObjects.find(o => o.type === 'wall' && o.x === newX && o.y === newY && o.active);
    if (hitWall) return;
    
    // Move player
    setPlayerPos({ x: newX, y: newY });
    
    // Check collisions
    checkCollisions(newX, newY);
  }, [isPlaying, isPaused, playerPos, gameObjects, gameStatus]);
  
  // Handle keyboard input
  useEffect(() => {
    if (!isPlaying || isPaused || gameStatus !== 'playing') return;
    
    const handleKeyDown = (e) => {
      let direction = null;
      
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          direction = 'up';
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          direction = 'down';
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          direction = 'left';
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          direction = 'right';
          break;
        default:
          return;
      }
      
      e.preventDefault();
      movePlayer(direction);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, isPaused, gameStatus, movePlayer]);
  
  // Enemy movement interval for patrol behavior
  useEffect(() => {
    if (!isPlaying || isPaused || gameStatus !== 'playing') {
      if (enemyIntervalRef.current) {
        clearInterval(enemyIntervalRef.current);
        enemyIntervalRef.current = null;
      }
      return;
    }
    
    // Check if any enemies have patrol patterns
    const hasPatrols = gameObjects.some(o => o.type === 'enemy' && o.patrol);
    if (!hasPatrols) return;
    
    // Move enemies on interval
    enemyIntervalRef.current = setInterval(() => {
      setGameObjects(prev => prev.map(obj => {
        if (obj.type !== 'enemy' || !obj.patrol || !obj.active) return obj;
        
        const { axis, min, max, direction } = obj.patrol;
        let newPos = axis === 'x' ? obj.x : obj.y;
        newPos += direction;
        
        // Check if we hit a wall
        const hitWall = prev.some(o => 
          o.type === 'wall' && 
          ((axis === 'x' && o.x === newPos && o.y === obj.y) ||
           (axis === 'y' && o.y === newPos && o.x === obj.x))
        );
        
        // Reverse direction if we hit boundary or wall
        let newDirection = direction;
        if (newPos <= min || newPos >= max || hitWall) {
          newDirection = -direction;
          newPos = axis === 'x' ? obj.x : obj.y; // Don't move if reversing
        }
        
        // Check if enemy hits player
        const newX = axis === 'x' ? newPos : obj.x;
        const newY = axis === 'y' ? newPos : obj.y;
        
        // Return updated enemy
        return {
          ...obj,
          x: newX,
          y: newY,
          patrol: { ...obj.patrol, direction: newDirection }
        };
      }));
      
      // Check if any enemy moved onto the player
      setGameObjects(currentObjects => {
        const playerX = playerPos.x;
        const playerY = playerPos.y;
        const enemyOnPlayer = currentObjects.find(o => 
          o.type === 'enemy' && o.active && o.x === playerX && o.y === playerY
        );
        
        if (enemyOnPlayer) {
          const enabledRules = rules.filter(r => r.enabled);
          const enemyRule = enabledRules.find(r => r.trigger === 'player_touches_enemy');
          if (enemyRule) {
            if (shieldActive) {
              setShieldActive(false);
              toast.success('Shield blocked the hit!');
              playSound('collect');
            } else if (enemyRule.action === 'lose_life') {
              setLives(prev => {
                const newLives = prev - 1;
                if (newLives <= 0) {
                  setGameStatus('lost');
                  playSound('lose');
                }
                return Math.max(0, newLives);
              });
              // Reset player position
              const player = objects.find(o => o.type === 'player');
              if (player) {
                setPlayerPos({ x: player.x, y: player.y });
              }
              playSound('hit');
            }
          }
        }
        return currentObjects;
      });
    }, 600); // Move every 600ms
    
    return () => {
      if (enemyIntervalRef.current) {
        clearInterval(enemyIntervalRef.current);
        enemyIntervalRef.current = null;
      }
    };
  }, [isPlaying, isPaused, gameStatus, playerPos, objects, rules, shieldActive]);

  const checkCollisions = (x, y) => {
    const enabledRules = rules.filter(r => r.enabled);
    let coinsCollected = 0;
    const totalCoins = gameObjects.filter(o => o.type === 'coin').length;
    
    gameObjects.forEach(obj => {
      if (!obj.active || obj.x !== x || obj.y !== y) return;
      
      // Coin collision
      if (obj.type === 'coin') {
        const coinRule = enabledRules.find(r => r.trigger === 'player_touches_coin');
        if (coinRule) {
          setScore(prev => {
            const newScore = prev + (coinRule.action === 'score_add' ? coinRule.value : 1);
            // Check score win condition
            const scoreWinRule = enabledRules.find(r => r.trigger === 'score_reaches' && r.action === 'win_game');
            if (scoreWinRule && newScore >= scoreWinRule.value) {
              setGameStatus('won');
              playSound('win');
            }
            return newScore;
          });
          setGameObjects(prev => {
            const updated = prev.map(o => o.id === obj.id ? { ...o, active: false } : o);
            // Check all coins collected
            const remainingCoins = updated.filter(o => o.type === 'coin' && o.active).length;
            const allCoinsRule = enabledRules.find(r => r.trigger === 'all_coins_collected');
            if (allCoinsRule && remainingCoins === 0) {
              setGameStatus('won');
              playSound('win');
            }
            return updated;
          });
          playSound('collect');
        }
      }
      
      // Enemy collision
      if (obj.type === 'enemy') {
        const enemyRule = enabledRules.find(r => r.trigger === 'player_touches_enemy');
        if (enemyRule) {
          // Check if shield is active
          if (shieldActive) {
            setShieldActive(false);
            toast.success('Shield blocked the hit!');
            playSound('collect');
            return; // Shield absorbs the hit
          }
          
          if (enemyRule.action === 'lose_life') {
            setLives(prev => {
              const newLives = prev - 1;
              if (newLives <= 0) {
                setGameStatus('lost');
                playSound('lose');
              }
              return Math.max(0, newLives);
            });
            // Reset player position
            const player = objects.find(o => o.type === 'player');
            if (player) {
              setPlayerPos({ x: player.x, y: player.y });
            }
          }
          playSound('hit');
        }
      }
      
      // Goal collision
      if (obj.type === 'goal') {
        const goalRule = enabledRules.find(r => r.trigger === 'player_touches_goal');
        if (goalRule && goalRule.action === 'win_game') {
          // Check if portal is locked and player has key
          if (portalLocked && !hasKey) {
            toast.error('Find the key to unlock the portal!');
            playSound('hit');
            return;
          }
          setGameStatus('won');
          playSound('win');
        }
      }
      
      // Heart collision - gain extra life
      if (obj.type === 'heart') {
        const heartRule = enabledRules.find(r => r.trigger === 'player_touches_heart');
        if (heartRule && heartRule.action === 'gain_life') {
          setLives(prev => Math.min(5, prev + 1)); // Max 5 lives
          setGameObjects(prev => prev.map(o => o.id === obj.id ? { ...o, active: false } : o));
          playSound('collect');
          toast.success('+1 Life!');
        }
      }
      
      // Key collision - unlock portal
      if (obj.type === 'key') {
        const keyRule = enabledRules.find(r => r.trigger === 'player_touches_key');
        if (keyRule) {
          setKeysCollected(prev => {
            const newCount = prev + 1;
            // Check if all keys collected
            if (newCount >= totalKeys) {
              setHasKey(true);
              setPortalLocked(false);
              toast.success('Portal Unlocked!');
            } else {
              toast.success(`Key ${newCount}/${totalKeys} collected!`);
            }
            return newCount;
          });
          setGameObjects(prev => prev.map(o => o.id === obj.id ? { ...o, active: false } : o));
          playSound('collect');
        }
      }
      
      // Gem collision - bonus score
      if (obj.type === 'gem') {
        const gemRule = enabledRules.find(r => r.trigger === 'player_touches_gem');
        if (gemRule) {
          const bonusValue = gemRule.action === 'bonus_score' ? (gemRule.value || 5) : 5;
          setScore(prev => {
            const newScore = prev + bonusValue;
            // Check score win condition
            const scoreWinRule = enabledRules.find(r => r.trigger === 'score_reaches' && r.action === 'win_game');
            if (scoreWinRule && newScore >= scoreWinRule.value) {
              setGameStatus('won');
              playSound('win');
            }
            return newScore;
          });
          setGameObjects(prev => prev.map(o => o.id === obj.id ? { ...o, active: false } : o));
          playSound('collect');
          toast.success(`+${bonusValue} Bonus!`);
        }
      }
      
      // Speed Boost collision
      if (obj.type === 'speedBoost') {
        const speedRule = enabledRules.find(r => r.trigger === 'player_touches_speedBoost');
        if (speedRule && speedRule.action === 'speed_boost') {
          setSpeedBoostActive(true);
          setGameObjects(prev => prev.map(o => o.id === obj.id ? { ...o, active: false } : o));
          playSound('collect');
          toast.success('Speed Boost!');
          
          // Clear existing timer
          if (speedBoostTimer) clearTimeout(speedBoostTimer);
          
          // Set new timer to deactivate after 5 seconds
          const timer = setTimeout(() => {
            setSpeedBoostActive(false);
          }, 5000);
          setSpeedBoostTimer(timer);
        }
      }
      
      // Shield collision
      if (obj.type === 'shield') {
        const shieldRule = enabledRules.find(r => r.trigger === 'player_touches_shield');
        if (shieldRule && shieldRule.action === 'activate_shield') {
          setShieldActive(true);
          setGameObjects(prev => prev.map(o => o.id === obj.id ? { ...o, active: false } : o));
          playSound('collect');
          toast.success('Shield activated!');
        }
      }
    });
  };
  
  // Award sparks on win
  useEffect(() => {
    if (gameStatus === 'won' && !sparksAwarded && onAwardSparks) {
      setSparksAwarded(true);
      // Award 10 sparks for winning, 20 for saving
    }
  }, [gameStatus, sparksAwarded, onAwardSparks]);
  
  // ============ SAVE/LOAD ============
  const saveGame = async () => {
    if (!selectedChild?.id) {
      toast.error('No child profile selected');
      return;
    }
    
    if (objects.length === 0) {
      toast.error('Add some objects to your game first!');
      return;
    }
    
    // Validate game has at least a player
    if (!objects.find(o => o.type === 'player')) {
      toast.error(`Add a ${assetPack.objects.player.name} to your game!`);
      return;
    }
    
    try {
      const gameData = {
        name: gameName,
        objects: objects.map(o => ({ id: o.id, type: o.type, x: o.x, y: o.y })),
        rules: rules.map(r => ({ id: r.id, trigger: r.trigger, action: r.action, value: r.value, enabled: r.enabled, description: '' })),
        child_id: selectedChild.id,
        creator_name: selectedChild.name
      };
      
      const response = await api.post('/games', gameData);
      const { sparks_awarded, is_new } = response.data;
      
      // Trigger save animation for visual feedback
      setSaveAnimation(is_new ? 'new' : 'updated');
      setTimeout(() => setSaveAnimation(null), 3000);
      
      if (is_new) {
        // Big celebration for new game!
        toast.success(
          <div className="flex flex-col items-center gap-1">
            <span className="text-lg font-bold">🎉 New Game Created!</span>
            <span className="text-yellow-300 font-semibold">+{sparks_awarded} Creativity Sparks!</span>
          </div>,
          { duration: 3000 }
        );
        if (onAwardSparks && sparks_awarded > 0) {
          await onAwardSparks(sparks_awarded, 'creativity');
        }
        playSound('achievement');
      } else {
        // Subtle confirmation for update
        toast.success(
          <div className="flex flex-col items-center gap-1">
            <span className="font-semibold">💾 Game Updated!</span>
            <span className="text-xs text-slate-400">Changes saved (no new sparks)</span>
          </div>,
          { duration: 2000 }
        );
      }
      
      // Reload saved games
      loadSavedGames();
    } catch (err) {
      console.error('Failed to save game:', err);
      toast.error('Failed to save game');
    }
  };

  // ============ CHALLENGE SYSTEM ============
  
  // Generate share code for a game
  const generateShareCode = async (gameId) => {
    try {
      const response = await api.post(`/games/${gameId}/share`);
      setShareCode(response.data.share_code);
      setShareUrl(response.data.share_url);
      setShowChallengeModal(true);
      toast.success('Challenge code generated!');
    } catch (err) {
      console.error('Failed to generate share code:', err);
      toast.error('Could not generate challenge code');
    }
  };
  
  // Load a challenge by code
  const loadChallenge = async (code) => {
    if (!code || code.length < 4) {
      toast.error('Please enter a valid challenge code');
      return;
    }
    
    setLoadingChallenge(true);
    try {
      const response = await api.get(`/games/challenge/${code.toUpperCase()}`);
      const challengeData = response.data;
      
      // Load the game
      setGameName(challengeData.name);
      setObjects(challengeData.objects.map((obj, idx) => ({
        ...obj,
        id: obj.id || `obj-${idx}`
      })));
      setRules(challengeData.rules || [...DEFAULT_RULES]);
      setChallengeCreator(challengeData.creator_name);
      setIsPlayingChallenge(true);
      setShowEnterChallengeModal(false);
      setChallengeCodeInput('');
      setShowTemplates(false);  // IMPORTANT: Hide template selection to show game grid
      setShowOnboarding(false); // Skip onboarding for challenges
      setActiveTab('build');
      
      toast.success(
        <div className="flex flex-col items-center gap-1">
          <span className="font-bold">Challenge from {challengeData.creator_name}!</span>
          <span className="text-sm">"{challengeData.name}"</span>
        </div>,
        { duration: 3000 }
      );
      
      // Auto-start after a short delay using the existing pattern
      setShouldStartAfterLoad(true);
      
    } catch (err) {
      console.error('Failed to load challenge:', err);
      const errorMsg = err.response?.data?.detail || 'Challenge not found. Check your code!';
      toast.error(errorMsg);
    } finally {
      setLoadingChallenge(false);
    }
  };
  
  // Copy share code to clipboard
  const copyShareCode = () => {
    if (shareCode) {
      navigator.clipboard.writeText(shareCode);
      toast.success('Code copied!');
    }
  };
  
  // Copy share URL to clipboard
  const copyShareUrl = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    }
  };


  
  // ============ RENDER GRID CELL ============
  const renderCell = (x, y, size = BASE_CELL_SIZE) => {
    const obj = isPlaying 
      ? (playerPos.x === x && playerPos.y === y) 
        ? { type: 'player' } 
        : gameObjects.find(o => o.x === x && o.y === y && o.active)
      : objects.find(o => o.x === x && o.y === y);
    
    const isSelected = selectedObject && selectedObject.x === x && selectedObject.y === y;
    const typeInfo = obj ? OBJECT_TYPES[obj.type] : null;
    const assetInfo = obj ? assetPack.objects[obj.type] : null;
    
    // Check if this is a locked portal
    const isLockedPortal = obj?.type === 'goal' && portalLocked && isPlaying;
    
    // Visual feedback states
    const isRecentlyPlaced = recentlyPlaced?.x === x && recentlyPlaced?.y === y;
    const isRecentlyRemoved = recentlyRemoved?.x === x && recentlyRemoved?.y === y;
    const isEraserHover = draggedType === 'eraser' && obj;
    
    // Responsive font size based on cell size
    const fontSize = size < 30 ? 'text-sm' : size < 35 ? 'text-base' : 'text-lg sm:text-xl';
    
    return (
      <div
        key={`${x}-${y}`}
        className={`relative border transition-all cursor-pointer ${
          isSelected ? 'ring-2 ring-cyan-400 z-10 border-cyan-400' : 'border-slate-700/20'
        } ${obj ? 'bg-slate-800/50' : 'hover:bg-slate-800/20'} ${
          isRecentlyPlaced ? 'animate-pulse ring-2 ring-green-400 bg-green-500/20' : ''
        } ${isRecentlyRemoved ? 'animate-ping bg-red-500/30' : ''} ${
          isEraserHover ? 'hover:bg-red-500/30 hover:border-red-400' : ''
        }`}
        style={{ width: size, height: size }}
        onClick={() => handleCellClick(x, y)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleCanvasDrop}
        data-testid={`cell-${x}-${y}`}
      >
        {obj && typeInfo && (
          <div 
            className={`absolute inset-0.5 sm:inset-1 rounded-md sm:rounded-lg flex items-center justify-center ${fontSize} transition-all ${
              isPlaying && obj.type === 'player' ? (speedBoostActive ? 'animate-bounce' : 'animate-pulse') : ''
            } ${isLockedPortal ? 'opacity-50' : ''} ${
              isRecentlyPlaced ? 'scale-125' : ''
            } ${isRecentlyRemoved ? 'scale-0 opacity-0' : ''}`}
            style={{ 
              backgroundColor: `${typeInfo.color}20`,
              boxShadow: isRecentlyPlaced 
                ? `0 0 20px #22c55e, 0 0 40px #22c55e`
                : isPlaying && obj.type === 'player' && shieldActive
                  ? `0 0 20px ${OBJECT_TYPES.shield.color}, 0 0 40px ${OBJECT_TYPES.shield.color}, inset 0 0 15px ${OBJECT_TYPES.shield.color}`
                  : speedBoostActive && obj.type === 'player' 
                    ? `0 0 20px ${OBJECT_TYPES.speedBoost.color}, 0 0 40px ${OBJECT_TYPES.speedBoost.color}`
                    : `0 0 ${size < 30 ? '5px' : '10px'} ${typeInfo.glow}`,
              transition: 'all 0.3s ease-out'
            }}
          >
            <span className={`${speedBoostActive && obj.type === 'player' ? 'animate-spin' : ''} ${isRecentlyPlaced ? 'animate-bounce' : ''}`}>
              {isLockedPortal ? '🔒' : assetInfo?.emoji}
            </span>
          </div>
        )}
        
        {/* Placement sparkle effect */}
        {isRecentlyPlaced && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="absolute w-full h-full bg-gradient-radial from-green-400/30 to-transparent animate-ping" />
          </div>
        )}
      </div>
    );
  };
  
  // ============ RENDER TEMPLATE SELECTION ============
  const [expandedCategory, setExpandedCategory] = useState(null);
  
  const renderTemplateSelection = () => (
    <div className="space-y-6">
      {/* Inject retro styles */}
      <style>{RetroStyles}</style>
      
      <div className="text-center">
        <div className="relative inline-block">
          <Gamepad2 className="w-16 h-16 mx-auto text-purple-400 mb-4 retro-glow" />
        </div>
        <h2 className="text-2xl sm:text-3xl retro-font text-white mb-2 retro-glow" style={{ color: '#A855F7' }}>
          GAME FORGE
        </h2>
        <p className="text-slate-400 text-sm">Choose a game type, then pick your level!</p>
      </div>
      
      {/* ====== FEATURED SHOWCASE GAME ====== */}
      <div 
        className="rounded-xl overflow-hidden relative"
        style={{ 
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(6, 182, 212, 0.2) 50%, rgba(34, 197, 94, 0.2) 100%)',
          border: '3px solid',
          borderImage: 'linear-gradient(90deg, #8B5CF6, #06B6D4, #22C55E, #F59E0B, #8B5CF6) 1',
          boxShadow: '0 0 40px rgba(139, 92, 246, 0.3), inset 0 0 40px rgba(0,0,0,0.3)'
        }}
      >
        {/* Animated background stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <div 
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
              style={{ 
                left: `${Math.random() * 100}%`, 
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                opacity: 0.3 + Math.random() * 0.4
              }}
            />
          ))}
        </div>
        
        <div className="relative p-6">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-6 h-6 text-amber-400" />
            <span className="text-amber-400 retro-font text-xs tracking-wider">FEATURED SHOWCASE</span>
          </div>
          
          <div className="flex flex-col lg:flex-row gap-6 items-center">
            {/* Game info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-4xl">{FEATURED_SHOWCASE_GAME.icon}</span>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white retro-glow" style={{ color: '#A855F7' }}>
                    {FEATURED_SHOWCASE_GAME.name}
                  </h3>
                  <p className="text-slate-400 text-xs">by {FEATURED_SHOWCASE_GAME.author}</p>
                </div>
              </div>
              
              <p className="text-slate-300 text-sm mb-4 max-w-lg">
                {FEATURED_SHOWCASE_GAME.description}
              </p>
              
              {/* Features badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {FEATURED_SHOWCASE_GAME.features.map(feature => (
                  <span 
                    key={feature}
                    className="px-2 py-1 text-xs rounded-full bg-purple-500/20 border border-purple-500/40 text-purple-300"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowShowcase(true);
                  setIsShowcaseGame(true);
                  setEnemyPatrols(FEATURED_SHOWCASE_GAME.enemyPatrols);
                  setObjects(FEATURED_SHOWCASE_GAME.objects.map((o, i) => ({ ...o, id: `showcase-${i}` })));
                  setRules(FEATURED_SHOWCASE_GAME.rules);
                  setGameName(FEATURED_SHOWCASE_GAME.name);
                  setShowTemplates(false);
                  setShouldStartAfterLoad(true); // Will trigger game start after objects load
                }}
                className="px-6 py-3 rounded-xl retro-font text-sm arcade-btn flex items-center gap-2"
                style={{
                  background: 'linear-gradient(180deg, #22C55E 0%, #16A34A 100%)',
                  color: 'white',
                  textShadow: '0 2px 0 rgba(0,0,0,0.3)'
                }}
                data-testid="play-showcase-btn"
              >
                <Play className="w-4 h-4" />
                PLAY NOW
              </button>
              
              <button
                onClick={() => {
                  setIsShowcaseGame(true);
                  setEnemyPatrols(FEATURED_SHOWCASE_GAME.enemyPatrols);
                  setObjects(FEATURED_SHOWCASE_GAME.objects.map((o, i) => ({ ...o, id: `showcase-${i}` })));
                  setRules(FEATURED_SHOWCASE_GAME.rules);
                  setGameName(`${FEATURED_SHOWCASE_GAME.name} (Remix)`);
                  setShowTemplates(false);
                  toast.success('Remix mode! Edit and make it your own!');
                }}
                className="px-6 py-3 rounded-xl retro-font text-sm arcade-btn flex items-center gap-2"
                style={{
                  background: 'linear-gradient(180deg, #8B5CF6 0%, #7C3AED 100%)',
                  color: 'white',
                  textShadow: '0 2px 0 rgba(0,0,0,0.3)'
                }}
                data-testid="remix-showcase-btn"
              >
                <Wand2 className="w-4 h-4" />
                REMIX THIS
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent" />
        <span className="text-slate-500 text-xs retro-font">OR BUILD YOUR OWN</span>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent" />
      </div>
      
      {/* Game Categories */}
      <div className="space-y-4">
        {TEMPLATE_CATEGORIES.map(category => (
          <div 
            key={category.id}
            className="rounded-xl overflow-hidden pixel-border"
            style={{ 
              background: `linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.9) 100%)`,
              boxShadow: expandedCategory === category.id ? `0 0 30px ${category.color}40` : `0 0 15px ${category.color}15`
            }}
          >
            {/* Category Header - Click to expand */}
            <button
              onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
              className="w-full p-4 flex items-center justify-between group transition-all hover:bg-white/5"
              data-testid={`category-${category.id}`}
            >
              <div className="flex items-center gap-4">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl pixel-pulse"
                  style={{ 
                    backgroundColor: `${category.color}30`,
                    boxShadow: `0 0 20px ${category.color}40`
                  }}
                >
                  {category.icon}
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-bold text-white group-hover:text-purple-300 transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-slate-400 text-sm">{category.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span 
                  className="text-xs px-3 py-1 rounded-full border"
                  style={{ 
                    backgroundColor: `${category.color}20`,
                    borderColor: `${category.color}40`,
                    color: category.color
                  }}
                >
                  {category.gameType}
                </span>
                <span className="text-slate-500 text-sm">{category.levels.length} levels</span>
                <ChevronDown 
                  className={`w-5 h-5 text-slate-400 transition-transform ${
                    expandedCategory === category.id ? 'rotate-180' : ''
                  }`} 
                />
              </div>
            </button>
            
            {/* Levels - Show when expanded */}
            {expandedCategory === category.id && (
              <div className="px-4 pb-4 pt-2 border-t border-slate-700/50">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {category.levels.map((level, idx) => (
                    <button
                      key={level.id}
                      onClick={() => loadTemplate({
                        ...level,
                        icon: category.icon,
                        color: category.color,
                        gameType: category.gameType
                      })}
                      className="relative p-4 rounded-xl text-left transition-all group/level overflow-hidden hover:scale-[1.02]"
                      style={{ 
                        background: `linear-gradient(135deg, rgba(30,41,59,0.8) 0%, rgba(15,23,42,0.9) 100%)`,
                        border: `2px solid ${category.color}30`
                      }}
                      data-testid={`template-${level.id}`}
                    >
                      {/* Level number badge */}
                      <div 
                        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center retro-font text-sm"
                        style={{ 
                          backgroundColor: `${category.color}30`,
                          color: category.color,
                          boxShadow: `0 0 10px ${category.color}40`
                        }}
                      >
                        {level.levelNum}
                      </div>
                      
                      {/* Difficulty indicator */}
                      <div className="flex gap-1 mb-2">
                        {[1, 2, 3].map(star => (
                          <Star 
                            key={star}
                            className={`w-4 h-4 ${
                              star <= level.levelNum 
                                ? 'text-amber-400 fill-amber-400' 
                                : 'text-slate-700'
                            }`}
                          />
                        ))}
                      </div>
                      
                      <h4 className="text-white font-bold mb-0.5 group-hover/level:text-purple-300 transition-colors">
                        {level.name}
                      </h4>
                      <p className="text-slate-500 text-xs mb-2">{level.subtitle}</p>
                      <p className="text-slate-400 text-xs mb-3">{level.description}</p>
                      
                      {/* Preview strip */}
                      <div 
                        className="px-2 py-1.5 rounded-lg text-center text-xs border"
                        style={{ 
                          backgroundColor: 'rgba(0,0,0,0.3)',
                          borderColor: `${category.color}30`
                        }}
                      >
                        <span className="text-slate-300 tracking-wider">{level.preview}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="text-center space-y-4">
        {/* Enter Challenge Code Button */}
        <button
          onClick={() => setShowEnterChallengeModal(true)}
          className="px-6 py-3 rounded-xl retro-font text-xs arcade-btn flex items-center justify-center gap-2 mx-auto"
          style={{
            background: 'linear-gradient(180deg, #06B6D4 0%, #0891B2 100%)',
            color: 'white',
            textShadow: '0 2px 0 rgba(0,0,0,0.3)'
          }}
          data-testid="enter-challenge-btn"
        >
          <Swords className="w-4 h-4" />
          ENTER CHALLENGE CODE
        </button>
        
        <button
          onClick={() => setShowTemplates(false)}
          className="px-8 py-4 rounded-xl retro-font text-sm arcade-btn"
          style={{
            background: 'linear-gradient(180deg, #A855F7 0%, #7C3AED 100%)',
            color: 'white',
            textShadow: '0 2px 0 rgba(0,0,0,0.3)'
          }}
          data-testid="start-scratch-btn"
        >
          START FROM SCRATCH
        </button>
        
        {savedGames.length > 0 && (
          <div>
            <p className="text-slate-500 text-sm mb-2">Or load a saved game:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {savedGames.slice(0, 3).map(game => (
                <button
                  key={game.id}
                  onClick={() => loadSavedGame(game)}
                  className="px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors text-sm"
                >
                  {game.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
  // ============ RENDER OBJECT PALETTE ============
  const renderPalette = () => (
    <div className="space-y-3">
      {/* Object Guide Button */}
      <button
        onClick={() => setShowObjectGuide(true)}
        className="w-full p-2 rounded-lg bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30 text-purple-300 hover:border-purple-400 transition-all text-sm flex items-center justify-center gap-2"
        data-testid="show-object-guide"
      >
        <HelpCircle className="w-4 h-4" />
        <span>What does each object do?</span>
      </button>
      
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Game Objects</h3>
        <button
          onClick={() => setShowAssetPacks(true)}
          className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
          data-testid="asset-packs-btn"
        >
          <Package className="w-3 h-3" />
          {assetPack.name}
        </button>
      </div>
      
      <div className="grid grid-cols-1 gap-2">
        {Object.entries(OBJECT_TYPES).map(([type, typeInfo]) => {
          const assetInfo = assetPack.objects[type];
          const count = objects.filter(o => o.type === type).length;
          const atMax = count >= typeInfo.maxCount;
          
          // Role descriptions for each object type
          const roleDescriptions = {
            player: "YOU control this! Move with arrow keys.",
            enemy: "AVOID these! Touching them costs a life.",
            coin: "COLLECT these! Each one adds to your score.",
            wall: "BLOCKS movement. Use to create mazes.",
            goal: "REACH this to WIN the game!"
          };
          
          return (
            <div
              key={type}
              draggable={!atMax}
              onDragStart={() => !atMax && handleDragStart(type)}
              onDragEnd={handleDragEnd}
              onClick={() => !atMax && setDraggedType(draggedType === type ? null : type)}
              onMouseEnter={() => setHoveredObject(type)}
              onMouseLeave={() => setHoveredObject(null)}
              className={`p-3 rounded-xl border transition-all relative ${
                atMax 
                  ? 'border-slate-700/30 opacity-50 cursor-not-allowed' 
                  : draggedType === type
                    ? 'border-purple-500 bg-purple-500/20 cursor-pointer ring-2 ring-purple-400'
                    : 'border-slate-700/50 cursor-grab hover:border-purple-500/50 active:cursor-grabbing'
              }`}
              style={{ backgroundColor: atMax ? 'transparent' : `${typeInfo.color}10` }}
              data-testid={`palette-${type}`}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-2xl shrink-0"
                  style={{ backgroundColor: `${typeInfo.color}30`, boxShadow: `0 0 8px ${typeInfo.glow}` }}
                >
                  {assetInfo?.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm font-semibold">{assetInfo?.name}</p>
                    <span 
                      className="text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap"
                      style={{ backgroundColor: `${typeInfo.color}30`, color: typeInfo.color }}
                    >
                      {count}/{typeInfo.maxCount}
                    </span>
                  </div>
                  <p className="text-slate-400 text-xs mt-0.5">{roleDescriptions[type]}</p>
                </div>
              </div>
              
              {/* Hover tooltip with expanded info */}
              {hoveredObject === type && !atMax && (
                <div className="absolute left-full ml-2 top-0 z-20 w-48 p-3 rounded-lg bg-slate-800 border border-slate-600 shadow-xl hidden lg:block">
                  <p className="text-xs text-slate-300">{assetInfo?.description}</p>
                  <p className="text-xs text-purple-400 mt-2">Click or drag to place</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {draggedType && (
        <div className="p-2 rounded-lg bg-purple-500/20 border border-purple-500/30 animate-pulse">
          <p className="text-xs text-purple-300 text-center font-medium">
            Click on the grid to place your {assetPack.objects[draggedType]?.name}!
          </p>
        </div>
      )}
      
      {/* Quick tip for beginners */}
      {objects.length === 0 && !draggedType && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <p className="text-xs text-amber-400">
            <strong>Tip:</strong> Every game needs a Hero! Click on "Hero" above, then click on the grid to place it.
          </p>
        </div>
      )}
      
      {/* Quick Actions */}
      <div className="space-y-2 pt-3 border-t border-slate-700/50">
        {selectedObject && (
          <button
            onClick={deleteSelectedObject}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-colors text-sm"
            data-testid="delete-selected-btn"
          >
            <Trash2 className="w-4 h-4" />
            Delete Selected
          </button>
        )}
        <button
          onClick={clearAllObjects}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:bg-slate-700 transition-colors text-sm"
          data-testid="clear-all-btn"
        >
          <RotateCcw className="w-4 h-4" />
          Clear All
        </button>
      </div>
    </div>
  );
  
  // ============ RENDER RULES PANEL ============
  const renderRulesPanel = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Game Rules</h3>
        <span className="text-xs text-slate-500">
          {rules.filter(r => r.enabled).length} active
        </span>
      </div>
      
      <div className="space-y-3">
        {rules.map(rule => (
          <LogicBlock
            key={rule.id}
            rule={rule}
            onToggle={toggleRule}
            onUpdateValue={updateRuleValue}
            onRemove={removeRule}
          />
        ))}
      </div>
      
      <RuleBuilder onAdd={addRule} existingRules={rules} />
      
      <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/30">
        <p className="text-xs text-slate-500">
          <span className="text-purple-400 font-medium">Tip:</span> Enable rules to make your game interactive. 
          The green rules are active, gray rules are disabled.
        </p>
      </div>
    </div>
  );
  
  // ============ RENDER GAME HUD ============
  const renderGameHUD = () => (
    <div className="flex items-center justify-between mb-4 p-3 sm:p-4 bg-slate-900/90 rounded-xl border border-slate-700/50 crt-effect" style={{ boxShadow: '0 0 20px rgba(139, 92, 246, 0.2)' }}>
      <style>{RetroStyles}</style>
      <div className="flex items-center gap-3 sm:gap-5 flex-wrap">
        {/* Game Title - Prominent during play */}
        <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-purple-500/20 border border-purple-500/40 min-w-0">
          <span className="text-purple-300 font-bold retro-font text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[150px]" data-testid="game-title-hud">
            {gameName || 'My Game'}
          </span>
        </div>
        
        {/* Score */}
        <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <Star className="w-4 h-4 text-amber-400" />
          <span className="text-amber-400 font-bold retro-font text-xs sm:text-sm">{score}</span>
        </div>
        
        {/* Lives */}
        <div className="flex items-center gap-1 px-3 py-1 rounded-lg bg-red-500/10 border border-red-500/30">
          {[...Array(5)].map((_, i) => (
            <Heart 
              key={i} 
              className={`w-4 h-4 transition-all ${i < lives ? 'text-red-500 fill-red-500' : 'text-slate-700'}`} 
            />
          ))}
        </div>
        
        {/* Key indicator */}
        {totalKeys > 0 && (
          <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${
            hasKey 
              ? 'bg-green-500/20 border-green-500/50' 
              : 'bg-amber-500/10 border-amber-500/30'
          }`}>
            <span className="text-lg">{hasKey ? '🔓' : '🔑'}</span>
            <span className={`retro-font text-xs ${hasKey ? 'text-green-400' : 'text-amber-400'}`}>
              {keysCollected}/{totalKeys}
            </span>
          </div>
        )}
        
        {/* Speed boost indicator */}
        {speedBoostActive && (
          <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-cyan-500/20 border border-cyan-500/50 animate-pulse">
            <Bolt className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 retro-font text-xs">FAST!</span>
          </div>
        )}
        
        {/* Shield indicator */}
        {shieldActive && (
          <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-emerald-500/20 border border-emerald-500/50 animate-pulse">
            <span className="text-lg">🛡️</span>
            <span className="text-emerald-400 retro-font text-xs">SHIELD</span>
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={togglePause}
          className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors arcade-btn"
          data-testid="pause-btn"
        >
          {isPaused ? <Play className="w-5 h-5 text-white" /> : <Pause className="w-5 h-5 text-white" />}
        </button>
        <button
          onClick={stopGame}
          className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors arcade-btn"
          data-testid="stop-game-btn"
        >
          <Home className="w-5 h-5 text-white" />
        </button>
      </div>
    </div>
  );
  
  // ============ RENDER WIN/LOSE SCREEN ============
  const renderGameEndScreen = () => (
    <div className="absolute inset-0 bg-slate-900/98 flex items-center justify-center z-20 rounded-2xl crt-effect">
      <style>{RetroStyles}</style>
      <div className="text-center p-6 max-w-md">
        {gameStatus === 'won' ? (
          <>
            {/* Win Celebration */}
            <div className="relative mb-6">
              <div className="text-6xl animate-bounce mb-2">🎉</div>
              <Trophy className="w-16 h-16 mx-auto text-amber-400 retro-glow animate-pulse" />
              {/* Confetti-like sparkles */}
              <div className="absolute -top-4 left-1/4 text-2xl animate-ping">✨</div>
              <div className="absolute -top-2 right-1/4 text-xl animate-ping delay-100">⭐</div>
              <div className="absolute top-8 left-0 text-lg animate-ping delay-200">🌟</div>
              <div className="absolute top-6 right-0 text-lg animate-ping delay-300">✨</div>
            </div>
            
            {/* Challenge-specific win message */}
            {isPlayingChallenge && challengeCreator ? (
              <>
                <h2 className="text-2xl sm:text-3xl retro-font text-amber-400 mb-2 retro-glow animate-pulse">
                  YOU BEAT {challengeCreator.toUpperCase()}'S CHALLENGE!
                </h2>
                <p className="text-slate-300 mb-2 text-lg">Amazing job, champion! ⚔️</p>
                <p className="text-purple-300 text-sm mb-4 font-semibold">
                  "{gameName}" by {challengeCreator}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-3xl sm:text-4xl retro-font text-amber-400 mb-2 retro-glow animate-pulse">
                  YOU DID IT!
                </h2>
                <p className="text-slate-300 mb-2 text-lg">Amazing job, champion! 🏆</p>
                <p className="text-purple-300 text-sm mb-4 font-semibold">
                  "{gameName || 'Your Game'}"
                </p>
              </>
            )}
            
            <div className="space-y-2 mb-4">
              <p className="text-slate-400 text-sm">Final Score</p>
              <p className="text-4xl retro-font text-white font-bold">{score}</p>
            </div>
            
            {/* Only show sparks earned for non-challenge games */}
            {!isPlayingChallenge && (
              <div className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500/30 to-cyan-500/30 border border-purple-400/50 inline-block mb-4 animate-pulse">
                <div className="flex items-center gap-2 text-purple-300">
                  <Sparkles className="w-5 h-5" />
                  <span className="retro-font text-lg">+10 SPARKS EARNED!</span>
                  <Sparkles className="w-5 h-5" />
                </div>
              </div>
            )}
            
            {/* Challenge win message */}
            {isPlayingChallenge && challengeCreator ? (
              <div className="mb-6 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30">
                <p className="text-amber-300 text-sm font-medium flex items-center justify-center gap-2">
                  <span className="text-lg">🏆</span>
                  Challenge Complete!
                </p>
                <p className="text-slate-400 text-xs mt-1">Tell {challengeCreator} you beat their game!</p>
              </div>
            ) : (
              <div className="mb-6 px-4 py-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
                <p className="text-cyan-300 text-sm font-medium flex items-center justify-center gap-2">
                  <span className="text-lg">👋</span>
                  Show someone your game!
                </p>
                <p className="text-slate-400 text-xs mt-1">They'd love to play it too!</p>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Lose - Encouraging Message */}
            <div className="text-6xl mb-4">🌟</div>
            
            {/* Challenge-specific lose message */}
            {isPlayingChallenge && challengeCreator ? (
              <>
                <h2 className="text-2xl sm:text-3xl retro-font text-cyan-400 mb-3">
                  NICE TRY!
                </h2>
                <p className="text-slate-300 mb-2 text-lg">{challengeCreator}'s challenge is tough!</p>
                <p className="text-purple-300 text-sm mb-2 font-medium">
                  "{gameName}" by {challengeCreator}
                </p>
                <p className="text-slate-400 mb-4 text-sm">Give it another shot! 💪</p>
              </>
            ) : (
              <>
                <h2 className="text-2xl sm:text-3xl retro-font text-cyan-400 mb-3">
                  NICE TRY!
                </h2>
                <p className="text-slate-300 mb-2 text-lg">Every hero falls sometimes.</p>
                <p className="text-purple-300 text-sm mb-2 font-medium">
                  "{gameName || 'Your Game'}"
                </p>
                <p className="text-slate-400 mb-4 text-sm">You'll get it next time! 💪</p>
              </>
            )}
            
            <div className="space-y-2 mb-4">
              <p className="text-slate-500 text-xs">Score</p>
              <p className="text-2xl retro-font text-white">{score}</p>
            </div>
            
            {/* Encouragement */}
            <div className="mb-6 px-4 py-2 rounded-xl bg-purple-500/10 border border-purple-500/30">
              <p className="text-purple-300 text-xs">
                {isPlayingChallenge ? `Can you beat ${challengeCreator}'s challenge?` : 'Keep practicing - you created something awesome!'}
              </p>
            </div>
          </>
        )}
        
        {/* Action Buttons - Play Again is primary */}
        <div className="flex flex-col gap-3">
          <button
            onClick={startGame}
            className="w-full px-8 py-4 rounded-xl retro-font text-lg arcade-btn transform hover:scale-105 transition-all"
            style={{
              background: gameStatus === 'won' 
                ? 'linear-gradient(180deg, #22C55E 0%, #16A34A 100%)'
                : 'linear-gradient(180deg, #A855F7 0%, #7C3AED 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(168, 85, 247, 0.4)'
            }}
            data-testid="play-again-btn"
          >
            🎮 PLAY AGAIN
          </button>
          <button
            onClick={stopGame}
            className="w-full px-6 py-3 rounded-xl retro-font text-sm arcade-btn bg-slate-700/80 text-slate-300 hover:bg-slate-600 transition-all"
            data-testid="edit-game-btn"
          >
            ✏️ EDIT LEVEL
          </button>
        </div>
      </div>
    </div>
  );
  
  // ============ RENDER SAVE TAB ============
  const renderSaveTab = () => (
    <div className="space-y-6">
      <div className="glass-panel p-6 rounded-2xl">
        <h3 className="text-lg font-semibold text-white mb-4">Save Your Game</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Game Name</label>
            <input
              type="text"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white"
              placeholder="My Awesome Game"
              data-testid="game-name-input"
            />
          </div>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3">
            <div className="flex items-center gap-2 text-purple-400">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">+20 Creativity Sparks for saving!</span>
            </div>
          </div>
          <button
            onClick={saveGame}
            disabled={objects.length === 0}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-game-btn"
          >
            Save Game
          </button>
        </div>
      </div>
      
      <div className="text-center text-slate-500 text-sm">
        <p>Objects: {objects.length}</p>
        <p>Rules: {rules.filter(r => r.enabled).length} active</p>
      </div>
      
      {/* Saved Games List */}
      {savedGames.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Your Saved Games</h4>
          <div className="space-y-2">
            {savedGames.map(game => (
              <div 
                key={game.id}
                className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50"
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-white font-medium">{game.name}</p>
                    <p className="text-xs text-slate-500">{game.objects?.length || 0} objects</p>
                  </div>
                  <button
                    onClick={() => loadSavedGame(game)}
                    className="px-3 py-1 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 text-sm"
                  >
                    Load
                  </button>
                </div>
                {/* Challenge button */}
                <button
                  onClick={() => {
                    setCurrentGameId(game.id);
                    generateShareCode(game.id);
                  }}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-300 hover:from-purple-500/30 hover:to-pink-500/30 text-sm font-medium flex items-center justify-center gap-2 transition-all"
                  data-testid={`challenge-btn-${game.id}`}
                >
                  <Swords className="w-4 h-4" />
                  Challenge Someone
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  
  // ============ ASSET PACKS MODAL ============
  const renderAssetPacksModal = () => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowAssetPacks(false)}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md mx-4 w-full" onClick={e => e.stopPropagation()}>
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-purple-400" />
          Asset Packs
        </h3>
        <p className="text-slate-400 text-sm mb-4">Choose a theme for your game objects:</p>
        
        <div className="space-y-3">
          {Object.values(ASSET_PACKS).map(pack => (
            <button
              key={pack.id}
              onClick={() => {
                if (!pack.locked) {
                  setCurrentAssetPack(pack.id);
                  setShowAssetPacks(false);
                  toast.success(`Switched to ${pack.name}!`);
                }
              }}
              disabled={pack.locked}
              className={`w-full p-4 rounded-xl text-left transition-all ${
                pack.locked 
                  ? 'bg-slate-800/30 opacity-50 cursor-not-allowed'
                  : currentAssetPack === pack.id
                    ? 'bg-purple-500/20 border-2 border-purple-500'
                    : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-3xl">{pack.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium">{pack.name}</p>
                    {pack.locked && <Lock className="w-4 h-4 text-slate-500" />}
                  </div>
                  <p className="text-slate-500 text-sm">{pack.description}</p>
                </div>
                {currentAssetPack === pack.id && (
                  <span className="text-xs px-2 py-1 rounded-full bg-purple-500/30 text-purple-400">Active</span>
                )}
              </div>
            </button>
          ))}
        </div>
        
        <p className="text-xs text-slate-600 mt-4 text-center">
          More asset packs coming soon! Complete journeys to unlock themed packs.
        </p>
        
        <button
          onClick={() => setShowAssetPacks(false)}
          className="w-full mt-4 py-2 rounded-xl bg-slate-800 text-white hover:bg-slate-700 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );

  // ============ CHALLENGE SHARE MODAL ============
  const renderChallengeShareModal = () => {
    if (!showChallengeModal) return null;
    
    return (
      <div 
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
        onClick={() => setShowChallengeModal(false)}
      >
        <div 
          className="bg-slate-900 border border-purple-500/30 rounded-2xl p-6 max-w-md w-full"
          onClick={e => e.stopPropagation()}
        >
          <div className="text-center">
            {/* Header */}
            <div className="text-5xl mb-4">⚔️</div>
            <h3 className="text-2xl font-bold text-white mb-2">Challenge Created!</h3>
            <p className="text-slate-400 mb-6">Share this code with a friend to challenge them!</p>
            
            {/* Share Code Display */}
            <div className="bg-slate-800 rounded-xl p-6 mb-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Challenge Code</p>
              <p 
                className="text-4xl font-mono font-bold text-purple-400 tracking-[0.2em] select-all"
                data-testid="share-code"
              >
                {shareCode}
              </p>
            </div>
            
            {/* Copy Buttons */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={copyShareCode}
                className="flex-1 py-3 rounded-xl bg-purple-500/20 border border-purple-500/50 text-purple-300 font-semibold hover:bg-purple-500/30 transition-colors flex items-center justify-center gap-2"
                data-testid="copy-code-btn"
              >
                <Copy className="w-4 h-4" />
                Copy Code
              </button>
              <button
                onClick={copyShareUrl}
                className="flex-1 py-3 rounded-xl bg-cyan-500/20 border border-cyan-500/50 text-cyan-300 font-semibold hover:bg-cyan-500/30 transition-colors flex items-center justify-center gap-2"
                data-testid="copy-link-btn"
              >
                <Link className="w-4 h-4" />
                Copy Link
              </button>
            </div>
            
            {/* Instructions */}
            <div className="bg-slate-800/50 rounded-xl p-4 text-left mb-4">
              <p className="text-sm text-slate-300 font-medium mb-2">How to challenge:</p>
              <ol className="text-sm text-slate-400 space-y-1">
                <li>1. Share the code with a friend</li>
                <li>2. They enter it in Game Forge</li>
                <li>3. They play your game!</li>
              </ol>
            </div>
            
            <p className="text-xs text-slate-500 mb-4">
              Code expires in 30 days
            </p>
            
            <button
              onClick={() => setShowChallengeModal(false)}
              className="w-full py-3 rounded-xl bg-slate-700 text-white hover:bg-slate-600 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // ============ ENTER CHALLENGE CODE MODAL ============
  const renderEnterChallengeModal = () => {
    if (!showEnterChallengeModal) return null;
    
    return (
      <div 
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
        onClick={() => setShowEnterChallengeModal(false)}
      >
        <div 
          className="bg-slate-900 border border-cyan-500/30 rounded-2xl p-6 max-w-md w-full"
          onClick={e => e.stopPropagation()}
        >
          <div className="text-center">
            {/* Header */}
            <div className="text-5xl mb-4">🎮</div>
            <h3 className="text-2xl font-bold text-white mb-2">Enter Challenge Code</h3>
            <p className="text-slate-400 mb-6">Got a code from a friend? Enter it here!</p>
            
            {/* Code Input */}
            <input
              type="text"
              value={challengeCodeInput}
              onChange={(e) => setChallengeCodeInput(e.target.value.toUpperCase().slice(0, 6))}
              placeholder="ABC123"
              className="w-full text-center text-3xl font-mono font-bold tracking-[0.3em] py-4 px-6 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 mb-6"
              maxLength={6}
              autoFocus
              data-testid="challenge-code-input"
            />
            
            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEnterChallengeModal(false);
                  setChallengeCodeInput('');
                }}
                className="flex-1 py-3 rounded-xl bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => loadChallenge(challengeCodeInput)}
                disabled={challengeCodeInput.length < 4 || loadingChallenge}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="load-challenge-btn"
              >
                {loadingChallenge ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Play Challenge
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };


  
  // ============ MAIN RENDER ============
  if (showTemplates) {
    return (
      <div className="space-y-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          data-testid="game-forge-back"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Courtyard</span>
        </button>
        {renderTemplateSelection()}
        
        {/* Modals need to be rendered even in template view */}
        {renderEnterChallengeModal()}
        {renderChallengeShareModal()}
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button
          onClick={() => setShowTemplates(true)}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          data-testid="back-to-templates"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="hidden sm:inline">Templates</span>
        </button>
        
        <div className="flex items-center gap-2 sm:gap-4 flex-1 justify-center">
          <input
            type="text"
            value={gameName}
            onChange={(e) => setGameName(e.target.value)}
            className="px-3 sm:px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-xl text-white text-center font-semibold max-w-[200px] sm:max-w-none"
            placeholder="Game Name"
            data-testid="game-name-header"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="p-2 rounded-lg bg-slate-800/50 hover:bg-slate-700 transition-colors"
            data-testid="sound-toggle"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5 text-slate-400" /> : <VolumeX className="w-5 h-5 text-slate-500" />}
          </button>
          <button
            onClick={() => setShowHelp(true)}
            className="p-2 rounded-lg bg-slate-800/50 hover:bg-slate-700 transition-colors"
            data-testid="help-btn"
          >
            <HelpCircle className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </div>
      
      {/* Tab Buttons */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'build', label: 'Build', icon: LayoutGrid },
          { id: 'rules', label: 'Rules', icon: Zap },
          { id: 'test', label: 'Test', icon: Play },
          { id: 'save', label: 'Save', icon: Save }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              if (tab.id === 'test') {
                startGame();
              } else {
                setActiveTab(tab.id);
                if (isPlaying) stopGame();
              }
            }}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-purple-500 text-white'
                : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="text-sm">{tab.label}</span>
          </button>
        ))}
      </div>
      
      {/* Main Content - Redesigned for better build tool proximity */}
      <div ref={containerRef} className="flex flex-col gap-2">
        {/* Quick Object Toolbar - Sticky and tightly connected to grid */}
        {activeTab === 'build' && !isPlaying && (
          <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 p-2 sm:p-3 rounded-xl bg-slate-800/95 backdrop-blur-sm border border-purple-500/30 shadow-lg">
            <span className="text-xs text-slate-500 mr-1 hidden sm:inline">Build:</span>
            {Object.entries(OBJECT_TYPES).map(([type, typeInfo]) => {
              const assetInfo = assetPack.objects[type];
              const count = objects.filter(o => o.type === type).length;
              const atMax = count >= typeInfo.maxCount;
              const isActive = draggedType === type;
              
              return (
                <button
                  key={type}
                  onClick={() => !atMax && setDraggedType(isActive ? null : type)}
                  disabled={atMax}
                  className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm transition-all tv-tool ${
                    atMax 
                      ? 'opacity-40 cursor-not-allowed bg-slate-800' 
                      : isActive
                        ? 'bg-purple-500 text-white ring-2 ring-purple-400 scale-105'
                        : 'bg-slate-700 hover:bg-slate-600 text-white hover:scale-102'
                  }`}
                  title={assetInfo?.name}
                  data-testid={`quick-${type}`}
                  data-nav-item
                  tabIndex={0}
                >
                  <span className="text-base">{assetInfo?.emoji}</span>
                  <span className="hidden md:inline text-xs">{assetInfo?.name}</span>
                  <span className="text-[10px] opacity-60 ml-0.5">{count}/{typeInfo.maxCount}</span>
                </button>
              );
            })}
            
            {/* Eraser/Delete mode */}
            <div className="h-5 w-px bg-slate-600 mx-0.5" />
            <button
              onClick={() => setDraggedType(draggedType === 'eraser' ? null : 'eraser')}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm transition-all ${
                draggedType === 'eraser'
                  ? 'bg-red-500 text-white ring-2 ring-red-400 scale-105'
                  : 'bg-slate-700 hover:bg-red-500/50 text-white hover:scale-102'
              }`}
              title="Eraser - Click cells to remove objects"
              data-testid="eraser-tool"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden md:inline text-xs">Eraser</span>
            </button>
            
            {/* Clear all */}
            <button
              onClick={clearAllObjects}
              disabled={objects.length === 0}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-40 transition-all"
              title="Clear all objects"
              data-testid="clear-all-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {/* Placement hint */}
        {draggedType && draggedType !== 'eraser' && (
          <div className="p-2 rounded-lg bg-purple-500/20 border border-purple-500/30 text-center">
            <p className="text-xs text-purple-300 font-medium">
              Click on the grid to place your {assetPack.objects[draggedType]?.name}! Click the button again to deselect.
            </p>
          </div>
        )}
        {draggedType === 'eraser' && (
          <div className="p-2 rounded-lg bg-red-500/20 border border-red-500/30 text-center">
            <p className="text-xs text-red-300 font-medium">
              Eraser mode: Click any object to remove it and create empty space.
            </p>
          </div>
        )}
        
        {/* Save Feedback Animation Banner */}
        {saveAnimation && (
          <div className={`p-4 rounded-xl border-2 text-center transition-all animate-pulse ${
            saveAnimation === 'new' 
              ? 'bg-gradient-to-r from-green-500/20 via-yellow-500/20 to-green-500/20 border-green-400' 
              : 'bg-blue-500/20 border-blue-400'
          }`}>
            {saveAnimation === 'new' ? (
              <div className="flex flex-col items-center gap-2">
                <div className="text-3xl animate-bounce">🎉</div>
                <p className="text-lg font-bold text-green-300">New Game Created!</p>
                <p className="text-yellow-300 font-semibold flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  +20 Creativity Sparks Earned!
                  <Sparkles className="w-5 h-5" />
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="text-2xl">💾</div>
                <p className="text-base font-semibold text-blue-300">Game Updated!</p>
                <p className="text-xs text-slate-400">Your changes have been saved</p>
              </div>
            )}
          </div>
        )}
        
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Canvas */}
          <div className="flex-1 flex flex-col items-center">
          {isPlaying && renderGameHUD()}
          
          <div 
            ref={canvasRef}
            tabIndex={0}
            className="relative rounded-2xl overflow-hidden crt-effect pixel-border outline-none focus:ring-2 focus:ring-purple-500/50"
            style={{ 
              width: GRID_WIDTH * cellSize, 
              height: GRID_HEIGHT * cellSize,
              maxWidth: '100%',
              background: 'linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(30,20,60,0.9) 100%)',
              boxShadow: isPlaying 
                ? `0 0 60px rgba(139, 92, 246, 0.3), inset 0 0 100px rgba(0,0,0,0.5)`
                : '0 0 40px rgba(139, 92, 246, 0.15)'
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleCanvasDrop}
            onClick={() => canvasRef.current?.focus()}
            onKeyDown={(e) => {
              if (!isPlaying || isPaused || gameStatus !== 'playing') return;
              let direction = null;
              switch (e.key) {
                case 'ArrowUp':
                case 'w':
                case 'W':
                  direction = 'up';
                  break;
                case 'ArrowDown':
                case 's':
                case 'S':
                  direction = 'down';
                  break;
                case 'ArrowLeft':
                case 'a':
                case 'A':
                  direction = 'left';
                  break;
                case 'ArrowRight':
                case 'd':
                case 'D':
                  direction = 'right';
                  break;
                default:
                  return;
              }
              e.preventDefault();
              movePlayer(direction);
            }}
            data-testid="game-canvas"
          >
            {/* Grid */}
            <div className="grid" style={{ gridTemplateColumns: `repeat(${GRID_WIDTH}, ${cellSize}px)` }}>
              {[...Array(GRID_HEIGHT)].map((_, y) => 
                [...Array(GRID_WIDTH)].map((_, x) => renderCell(x, y, cellSize))
              )}
            </div>
            
            {/* Pause overlay */}
            {isPlaying && isPaused && (
              <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center z-10">
                <div className="text-center">
                  <Pause className="w-12 h-12 mx-auto text-white mb-2" />
                  <p className="text-white font-semibold">Paused</p>
                  <p className="text-slate-400 text-sm">Press any arrow key to continue</p>
                </div>
              </div>
            )}
            
            {/* Countdown Overlay - Play Mode Transition */}
            {playModeCountdown !== null && (
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900/95 via-slate-900/95 to-cyan-900/95 flex items-center justify-center z-30 rounded-2xl">
                <div className="text-center">
                  {playModeCountdown === 'GO!' ? (
                    <>
                      <div className="text-7xl sm:text-8xl font-bold text-green-400 animate-bounce retro-font retro-glow">
                        GO!
                      </div>
                      <div className="text-2xl text-white/80 mt-4">🎮 Time to play!</div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-purple-300 uppercase tracking-widest mb-4 animate-pulse">
                        Get Ready...
                      </div>
                      <div 
                        className="text-8xl sm:text-9xl font-bold text-white retro-font animate-ping"
                        style={{ textShadow: '0 0 40px rgba(168, 85, 247, 0.8), 0 0 80px rgba(168, 85, 247, 0.4)' }}
                      >
                        {playModeCountdown}
                      </div>
                      <div className="mt-6 flex justify-center gap-2">
                        {[1, 2, 3].map(n => (
                          <div 
                            key={n}
                            className={`w-3 h-3 rounded-full transition-all ${
                              n <= (4 - playModeCountdown) ? 'bg-purple-400 scale-125' : 'bg-slate-600'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Win/Lose overlay */}
            {isPlaying && gameStatus !== 'playing' && renderGameEndScreen()}
          </div>
          
          {/* Controls hint (keyboard) or Touch controls */}
          {isPlaying && gameStatus === 'playing' && !isPaused && (
            <>
              {/* Show keyboard hint only on large screens (desktops) - hide on tablets/mobile */}
              {!isTouchDevice && (
                <p className="text-center text-slate-500 text-sm mt-2 hidden lg:block">
                  Use Arrow Keys or WASD to move
                </p>
              )}
              {/* Show touch controls on touch devices OR tablet/mobile screen sizes */}
              <MobileControls 
                onMove={movePlayer} 
                disabled={isPaused || gameStatus !== 'playing'} 
                isTouchDevice={isTouchDevice}
              />
            </>
          )}
          </div>
        
        {/* Sidebar */}
        {!isPlaying && (
          <div className="w-full lg:w-72 space-y-4">
            {activeTab === 'build' && renderPalette()}
            {activeTab === 'rules' && renderRulesPanel()}
            {activeTab === 'save' && renderSaveTab()}
          </div>
        )}
        </div>
      </div>
      
      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowHelp(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-4">How to Build a Game</h3>
            <div className="space-y-3 text-slate-300 text-sm">
              <p><strong className="text-purple-400">1. Build Tab:</strong> Drag objects from the palette onto the grid, or click to select and place them.</p>
              <p><strong className="text-purple-400">2. Rules Tab:</strong> Enable rules to define how your game works. Create custom rules with the Rule Builder.</p>
              <p><strong className="text-purple-400">3. Test Tab:</strong> Play your game! Use arrow keys (or WASD) to move. On mobile, use the on-screen controls.</p>
              <p><strong className="text-purple-400">4. Save Tab:</strong> Save your creation to earn Sparks and play later!</p>
            </div>
            <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <p className="text-amber-400 text-sm">
                <strong>Tip:</strong> Every game needs at least a {assetPack.objects.player.emoji} {assetPack.objects.player.name}!
              </p>
            </div>
            <button
              onClick={() => setShowHelp(false)}
              className="w-full mt-4 py-2 rounded-xl bg-slate-800 text-white hover:bg-slate-700 transition-colors"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
      
      {/* Object Guide Modal - Detailed explanations of each object */}
      {showObjectGuide && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowObjectGuide(false)}>
          <div className="bg-slate-900 border border-purple-500/30 rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Gamepad2 className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Game Objects Guide</h3>
                <p className="text-slate-400 text-sm">Learn what each piece does!</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Player/Hero */}
              <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{assetPack.objects.player.emoji}</span>
                  <div>
                    <h4 className="text-green-400 font-bold text-lg">{assetPack.objects.player.name}</h4>
                    <span className="text-xs text-green-300/70">Required - Only 1 allowed</span>
                  </div>
                </div>
                <p className="text-slate-300 text-sm">
                  <strong>This is YOU!</strong> The player controls this character using arrow keys (or WASD). 
                  Move around the grid to collect stars, avoid enemies, and reach the goal!
                </p>
              </div>
              
              {/* Enemy/Guardian */}
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{assetPack.objects.enemy.emoji}</span>
                  <div>
                    <h4 className="text-red-400 font-bold text-lg">{assetPack.objects.enemy.name}</h4>
                    <span className="text-xs text-red-300/70">Optional - Up to 10</span>
                  </div>
                </div>
                <p className="text-slate-300 text-sm">
                  <strong>Danger!</strong> If the Hero touches an enemy, they lose a life! 
                  Place these strategically to make your game more challenging.
                </p>
              </div>
              
              {/* Coin/Star */}
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{assetPack.objects.coin.emoji}</span>
                  <div>
                    <h4 className="text-amber-400 font-bold text-lg">{assetPack.objects.coin.name}</h4>
                    <span className="text-xs text-amber-300/70">Optional - Up to 20</span>
                  </div>
                </div>
                <p className="text-slate-300 text-sm">
                  <strong>Collect these!</strong> When the Hero touches a star, your score goes up. 
                  You can set the game to win when you collect enough stars!
                </p>
              </div>
              
              {/* Wall/Stone */}
              <div className="p-4 rounded-xl bg-slate-500/10 border border-slate-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{assetPack.objects.wall.emoji}</span>
                  <div>
                    <h4 className="text-slate-400 font-bold text-lg">{assetPack.objects.wall.name}</h4>
                    <span className="text-xs text-slate-300/70">Optional - Up to 50</span>
                  </div>
                </div>
                <p className="text-slate-300 text-sm">
                  <strong>Blocks movement!</strong> Neither the Hero nor enemies can walk through walls. 
                  Use them to create mazes, obstacles, and puzzle paths!
                </p>
              </div>
              
              {/* Goal/Portal */}
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{assetPack.objects.goal.emoji}</span>
                  <div>
                    <h4 className="text-blue-400 font-bold text-lg">{assetPack.objects.goal.name}</h4>
                    <span className="text-xs text-blue-300/70">Optional - Only 1 allowed</span>
                  </div>
                </div>
                <p className="text-slate-300 text-sm">
                  <strong>The finish line!</strong> When the Hero reaches the portal, you win the game! 
                  Place it somewhere the player has to work to get to.
                </p>
              </div>
            </div>
            
            <div className="mt-6 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
              <p className="text-purple-300 text-sm">
                <strong>Quick Start:</strong> Add a Hero, some Stars to collect, maybe a Portal to reach, 
                and press Test to play your game!
              </p>
            </div>
            
            <button
              onClick={() => setShowObjectGuide(false)}
              className="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold hover:opacity-90 transition-opacity"
              data-testid="close-object-guide"
            >
              Got it! Let me build!
            </button>
          </div>
        </div>
      )}
      
      {/* Asset Packs Modal */}
      {showAssetPacks && renderAssetPacksModal()}
      
      {/* Challenge Share Modal */}
      {renderChallengeShareModal()}
      
      {/* Enter Challenge Code Modal */}
      {renderEnterChallengeModal()}
    </div>
  );
};

export default GameForge;
